import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { supabase } from './supabase';

// ══════════════════════════════════════════════════════════════
//  DESIGN SYSTEM  (matches Timeless Tile Pro CRM)
// ══════════════════════════════════════════════════════════════
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --stone-950:#1a1714;--stone-900:#241f1b;--stone-800:#332d27;--stone-700:#4a4238;
  --stone-600:#6b5e52;--stone-500:#8c7b6e;--stone-400:#b0a095;--stone-300:#cfc4ba;
  --stone-200:#e8e0d8;--stone-100:#f4f0eb;--stone-50:#faf8f5;
  --warm-gold:#c9a96e;--warm-gold-light:#dfc08a;
  --accent-green:#7a9e7e;--accent-red:#c0614a;--accent-blue:#6b8cae;--accent-amber:#c4954a;
  --shadow-sm:0 1px 3px rgba(26,23,20,.12);--shadow-md:0 4px 12px rgba(26,23,20,.15);
  --shadow-lg:0 8px 28px rgba(26,23,20,.18);
  --radius:8px;--radius-lg:14px;
  --font-display:'Cormorant Garamond',serif;--font-body:'DM Sans',sans-serif;
}
body{background:var(--stone-100);color:var(--stone-900);font-family:var(--font-body)}
::-webkit-scrollbar{width:6px;height:6px}::-webkit-scrollbar-track{background:var(--stone-200)}::-webkit-scrollbar-thumb{background:var(--stone-400);border-radius:3px}
@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes modalIn{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:scale(1)}}
@keyframes slideDown{from{opacity:0;max-height:0}to{opacity:1;max-height:2000px}}
.tile-bg{background-image:linear-gradient(45deg,rgba(201,169,110,.04) 25%,transparent 25%),linear-gradient(-45deg,rgba(201,169,110,.04) 25%,transparent 25%),linear-gradient(45deg,transparent 75%,rgba(201,169,110,.04) 75%),linear-gradient(-45deg,transparent 75%,rgba(201,169,110,.04) 75%);background-size:20px 20px;background-position:0 0,0 10px,10px -10px,-10px 0}
input,select,textarea{width:100%;padding:8px 10px;border:1.5px solid var(--stone-300);border-radius:var(--radius);font-family:var(--font-body);font-size:13px;background:var(--stone-50);color:var(--stone-900);outline:none;transition:border-color .2s}
input:focus,select:focus,textarea:focus{border-color:var(--warm-gold)}
input[type="number"]{-moz-appearance:textfield}
input[type="number"]::-webkit-inner-spin-button{-webkit-appearance:none}
`;

// ══════════════════════════════════════════════════════════════
//  CATALOG DATA (from Phase 1)
// ══════════════════════════════════════════════════════════════
const CATALOG_CATS = {
  "Tile":                 { color:"#c9a96e", bg:"rgba(201,169,110,.12)", icon:"🏛️" },
  "Adhesive & Grout":     { color:"#6b8cae", bg:"rgba(107,140,174,.12)", icon:"🪣" },
  "Waterproofing":        { color:"#7b6bc4", bg:"rgba(123,107,196,.12)", icon:"🛡️" },
  "Subfloor & Prep":      { color:"#8c7b6e", bg:"rgba(140,123,110,.12)", icon:"📐" },
  "Lumber & Framing":     { color:"#946c00", bg:"rgba(148,108,0,.12)",   icon:"🪵" },
  "Hardware & Fasteners": { color:"#6b5e52", bg:"rgba(107,94,82,.12)",   icon:"🔩" },
  "Fixtures & Plumbing":  { color:"#0b7285", bg:"rgba(11,114,133,.12)",  icon:"🚿" },
  "Cabinets & Counters":  { color:"#c4954a", bg:"rgba(196,149,74,.12)",  icon:"🗄️" },
  "Equipment Rental":     { color:"#c92a2a", bg:"rgba(201,42,42,.12)",   icon:"🏗️" },
  "Electrical & Lighting":{ color:"#e67700", bg:"rgba(230,119,0,.12)",   icon:"💡" },
  "Glass":                { color:"#1a6b2a", bg:"rgba(26,107,42,.12)",   icon:"🪟" },
  "Doors & Trim":         { color:"#6a1b9a", bg:"rgba(106,27,154,.12)",  icon:"🚪" },
  "Cleaning Supplies":    { color:"#3b5bdb", bg:"rgba(59,91,219,.12)",   icon:"🧹" },
  "Paint & Finish":       { color:"#2d6a4f", bg:"rgba(45,106,79,.12)",   icon:"🎨" },
  "Other":                { color:"#8c7b6e", bg:"rgba(140,123,110,.12)", icon:"📦" },
};

// Catalog items are loaded live from the materials_catalog Supabase table
// (see loadCatalog() inside the component). Keeping this hardcoded causes
// newly-added catalog items from the Materials Catalog page to never appear here.

// ══════════════════════════════════════════════════════════════
//  WBS PHASE TEMPLATES (from Excel analysis)
// ══════════════════════════════════════════════════════════════
const PHASE_TEMPLATES = {
  bath: [
    { name:"Demo",             icon:"🔨", wbs:"1" },
    { name:"Subfloor Prep",    icon:"📐", wbs:"2" },
    { name:"Waterproofing",    icon:"🛡️", wbs:"3" },
    { name:"Tile Installation",icon:"🏛️", wbs:"4" },
    { name:"Fixtures",         icon:"🚿", wbs:"5" },
    { name:"Cabinets",         icon:"🗄️", wbs:"6" },
    { name:"Subcontractor",    icon:"👷", wbs:"7" },
  ],
  kitchen: [
    { name:"Demo",             icon:"🔨", wbs:"1" },
    { name:"Subfloor Prep",    icon:"📐", wbs:"2" },
    { name:"Framing & Drywall",icon:"🪵", wbs:"3" },
    { name:"Tile Installation",icon:"🏛️", wbs:"4" },
    { name:"Cabinets & Counters",icon:"🗄️",wbs:"5"},
    { name:"Fixtures & Plumbing",icon:"🚿",wbs:"6"},
    { name:"Electrical",       icon:"💡", wbs:"7" },
    { name:"Subcontractor",    icon:"👷", wbs:"8" },
  ],
  custom: [
    { name:"Demo",             icon:"🔨", wbs:"1" },
    { name:"Prep Work",        icon:"📐", wbs:"2" },
    { name:"Materials",        icon:"🏛️", wbs:"3" },
    { name:"Fixtures",         icon:"🚿", wbs:"4" },
    { name:"Subcontractor",    icon:"👷", wbs:"5" },
  ]
};

// ══════════════════════════════════════════════════════════════
//  DEMO PROJECTS (seeded from actual Excel data)
// ══════════════════════════════════════════════════════════════
const initProjects = [
  {
    id:1, name:"Sarah", customer:"Sarah Johnson", location:"Virginia Beach", type:"bath",
    laborType:"hourly", laborRate:22.50, laborHours:120, laborFlat:0,
    originalQuote:18000, changeOrder:0,
    phases:[
      { name:"Demo", icon:"🔨", wbs:"1", collapsed:false, laborBudget:300, laborActual:300, items:[
        { id:101,catalogId:51,name:"Dump Truck Load",cat:"Equipment Rental",unit:"ea",budgetQty:1,budgetPrice:325,actualQty:1,actualPrice:365,supplier:"Local Vendor" },
      ]},
      { name:"Subfloor Prep", icon:"📐", wbs:"2", collapsed:false, laborBudget:500, laborActual:500, items:[
        { id:102,catalogId:21,name:"Kerdi Fix Sealant",cat:"Waterproofing",unit:"ea",budgetQty:3,budgetPrice:30.70,actualQty:3,actualPrice:32.33,supplier:"Floor & Decor" },
        { id:103,catalogId:22,name:"Schluter Shower Curb",cat:"Waterproofing",unit:"ea",budgetQty:1,budgetPrice:73.27,actualQty:1,actualPrice:77.15,supplier:"The Tile Shop" },
        { id:104,catalogId:17,name:"Schluter Kerdi Ditra",cat:"Waterproofing",unit:"roll",budgetQty:2,budgetPrice:120.16,actualQty:2,actualPrice:125.33,supplier:"The Tile Shop" },
      ]},
      { name:"Waterproofing", icon:"🛡️", wbs:"3", collapsed:false, laborBudget:400, laborActual:450, items:[
        { id:105,catalogId:12,name:"Laticrete 254 Platinum Bond",cat:"Adhesive & Grout",unit:"bag",budgetQty:6,budgetPrice:36.98,actualQty:6,actualPrice:38.94,supplier:"Floor & Decor" },
        { id:106,catalogId:28,name:"Laticrete 3701 Mortar Bed",cat:"Subfloor & Prep",unit:"bag",budgetQty:2,budgetPrice:19.99,actualQty:2,actualPrice:21.05,supplier:"Floor & Decor" },
        { id:107,catalogId:27,name:"Wire Mesh 16ga",cat:"Subfloor & Prep",unit:"roll",budgetQty:1,budgetPrice:18.98,actualQty:1,actualPrice:19.99,supplier:"Home Depot" },
        { id:108,catalogId:26,name:"Mapei Self Level Plus",cat:"Subfloor & Prep",unit:"bag",budgetQty:2,budgetPrice:28.98,actualQty:2,actualPrice:30.52,supplier:"Home Depot" },
        { id:109,catalogId:23,name:"Schluter Trim Sticks",cat:"Waterproofing",unit:"ea",budgetQty:8,budgetPrice:27.27,actualQty:8,actualPrice:28.72,supplier:"The Tile Shop" },
        { id:110,catalogId:null,name:"Laticrete Perma Grout",cat:"Adhesive & Grout",unit:"bag",budgetQty:3,budgetPrice:31.39,actualQty:3,actualPrice:33.05,supplier:"Floor & Decor" },
        { id:111,catalogId:36,name:"Level Clips (400 pack)",cat:"Hardware & Fasteners",unit:"box",budgetQty:1,budgetPrice:89.99,actualQty:1,actualPrice:95.39,supplier:"Home Depot" },
        { id:112,catalogId:37,name:"Level Clip Tops",cat:"Hardware & Fasteners",unit:"box",budgetQty:2,budgetPrice:22.98,actualQty:2,actualPrice:24.36,supplier:"Home Depot" },
      ]},
      { name:"Tile Installation", icon:"🏛️", wbs:"4", collapsed:false, laborBudget:1200, laborActual:1350, items:[
        { id:113,catalogId:1,name:"Versatile Mid Grey Porcelain 12x24",cat:"Tile",unit:"ea",budgetQty:72,budgetPrice:13.79,actualQty:72,actualPrice:16.60,supplier:"The Tile Shop" },
        { id:114,catalogId:2,name:"Purity White Satin Porcelain 12x24",cat:"Tile",unit:"sqft",budgetQty:178,budgetPrice:4.49,actualQty:178.25,actualPrice:5.79,supplier:"Floor & Decor" },
        { id:115,catalogId:29,name:"2x4 Stud",cat:"Lumber & Framing",unit:"ea",budgetQty:2,budgetPrice:3.73,actualQty:2,actualPrice:3.93,supplier:"Home Depot" },
        { id:116,catalogId:18,name:"Schluter Kerdi Board",cat:"Waterproofing",unit:"ea",budgetQty:5,budgetPrice:108,actualQty:5,actualPrice:113.72,supplier:"The Tile Shop" },
        { id:117,catalogId:7,name:"Shower Floor Mosaic",cat:"Tile",unit:"ea",budgetQty:20,budgetPrice:26.49,actualQty:20,actualPrice:33.74,supplier:"The Tile Shop" },
        { id:118,catalogId:14,name:"Laticrete Silicone",cat:"Adhesive & Grout",unit:"ea",budgetQty:3,budgetPrice:15.29,actualQty:3,actualPrice:16.10,supplier:"Floor & Decor" },
      ]},
      { name:"Fixtures", icon:"🚿", wbs:"5", collapsed:false, laborBudget:300, laborActual:300, items:[
        { id:119,catalogId:45,name:"Base Cabinet w/ Stone Top",cat:"Cabinets & Counters",unit:"ea",budgetQty:2,budgetPrice:500,actualQty:2,actualPrice:500,supplier:"Wholesale Supply" },
        { id:120,catalogId:47,name:"Bathroom LED Mirror",cat:"Cabinets & Counters",unit:"ea",budgetQty:2,budgetPrice:150,actualQty:2,actualPrice:157.95,supplier:"Home Depot" },
        { id:121,catalogId:39,name:"Shower Fixtures (Matte Black)",cat:"Fixtures & Plumbing",unit:"ea",budgetQty:2,budgetPrice:204.72,actualQty:2,actualPrice:215.57,supplier:"Home Depot" },
        { id:122,catalogId:40,name:"Faucet Fixtures",cat:"Fixtures & Plumbing",unit:"ea",budgetQty:2,budgetPrice:175,actualQty:2,actualPrice:184.28,supplier:"Home Depot" },
        { id:123,catalogId:42,name:"Deep Soaking Bathtub",cat:"Fixtures & Plumbing",unit:"ea",budgetQty:1,budgetPrice:299,actualQty:1,actualPrice:314.85,supplier:"Home Depot" },
        { id:124,catalogId:48,name:"Frameless Glass Shower Door",cat:"Glass",unit:"ea",budgetQty:1,budgetPrice:1000,actualQty:1,actualPrice:1053,supplier:"Local Vendor" },
        { id:125,catalogId:43,name:"Toilet (Comfort Height)",cat:"Fixtures & Plumbing",unit:"ea",budgetQty:1,budgetPrice:250,actualQty:1,actualPrice:255,supplier:"Home Depot" },
        { id:126,catalogId:52,name:"Exhaust Vent Fans",cat:"Electrical & Lighting",unit:"ea",budgetQty:2,budgetPrice:62.99,actualQty:2,actualPrice:66.77,supplier:"Home Depot" },
        { id:127,catalogId:53,name:"Light Fixtures",cat:"Electrical & Lighting",unit:"ea",budgetQty:2,budgetPrice:75,actualQty:2,actualPrice:78.98,supplier:"Home Depot" },
      ]},
      { name:"Subcontractor", icon:"👷", wbs:"6", collapsed:false, laborBudget:0, laborActual:0, items:[
        { id:128,catalogId:null,name:"Tile Helper",cat:"Other",unit:"ea",budgetQty:1,budgetPrice:2700,actualQty:1,actualPrice:3200,supplier:"Sub" },
      ]},
    ],
  },
  {
    id:2, name:"Matthews", customer:"Matthews Family", location:"5111 Elmhurst St", type:"bath",
    laborType:"hourly", laborRate:31.25, laborHours:80, laborFlat:0,
    originalQuote:11500, changeOrder:4151,
    phases:[
      { name:"Demo", icon:"🔨", wbs:"1", collapsed:true, laborBudget:200, laborActual:200, items:[
        { id:201,catalogId:50,name:"Jack Hammer (daily rate)",cat:"Equipment Rental",unit:"day",budgetQty:2,budgetPrice:77,actualQty:2,actualPrice:77,supplier:"Home Depot" },
        { id:202,catalogId:51,name:"Dump Truck Load",cat:"Equipment Rental",unit:"ea",budgetQty:1,budgetPrice:500,actualQty:1,actualPrice:500,supplier:"Local Vendor" },
      ]},
      { name:"Subfloor Prep", icon:"📐", wbs:"2", collapsed:true, laborBudget:400, laborActual:400, items:[
        { id:203,catalogId:24,name:"Subfloor OSB 3/4 inch",cat:"Subfloor & Prep",unit:"sheet",budgetQty:2,budgetPrice:32.98,actualQty:2,actualPrice:32.98,supplier:"Home Depot" },
        { id:204,catalogId:null,name:"Subfloor Sealant",cat:"Subfloor & Prep",unit:"ea",budgetQty:2,budgetPrice:5.58,actualQty:2,actualPrice:5.58,supplier:"Home Depot" },
        { id:205,catalogId:32,name:"Greenboard Drywall",cat:"Lumber & Framing",unit:"sheet",budgetQty:6,budgetPrice:21.31,actualQty:6,actualPrice:21.31,supplier:"Home Depot" },
        { id:206,catalogId:29,name:"2x4 Stud",cat:"Lumber & Framing",unit:"ea",budgetQty:10,budgetPrice:3.73,actualQty:10,actualPrice:3.73,supplier:"Home Depot" },
      ]},
      { name:"Waterproofing", icon:"🛡️", wbs:"3", collapsed:true, laborBudget:350, laborActual:350, items:[
        { id:207,catalogId:16,name:"Schluter Kerdi Kit 48x72",cat:"Waterproofing",unit:"ea",budgetQty:1,budgetPrice:1000,actualQty:1,actualPrice:785,supplier:"The Tile Shop" },
        { id:208,catalogId:9,name:"Mapei Keraflex Thinset",cat:"Adhesive & Grout",unit:"bag",budgetQty:4,budgetPrice:25.58,actualQty:4,actualPrice:25.58,supplier:"Home Depot" },
        { id:209,catalogId:11,name:"Spectra Lock Epoxy Grout",cat:"Adhesive & Grout",unit:"ea",budgetQty:2,budgetPrice:99,actualQty:2,actualPrice:99,supplier:"The Tile Shop" },
        { id:210,catalogId:17,name:"Schluter Kerdi Ditra",cat:"Waterproofing",unit:"roll",budgetQty:1,budgetPrice:114,actualQty:1,actualPrice:114,supplier:"The Tile Shop" },
      ]},
      { name:"Tile Installation", icon:"🏛️", wbs:"4", collapsed:true, laborBudget:800, laborActual:800, items:[
        { id:211,catalogId:5,name:"Chastaint Ivory Porcelain",cat:"Tile",unit:"ea",budgetQty:15,budgetPrice:18.66,actualQty:15,actualPrice:22.33,supplier:"The Tile Shop" },
        { id:212,catalogId:4,name:"Dolomite Iceberg Marble",cat:"Tile",unit:"ea",budgetQty:9,budgetPrice:113.95,actualQty:9,actualPrice:101.29,supplier:"The Tile Shop" },
        { id:213,catalogId:6,name:"Bullnose Trim",cat:"Tile",unit:"ea",budgetQty:20,budgetPrice:5.49,actualQty:20,actualPrice:5.49,supplier:"The Tile Shop" },
        { id:214,catalogId:null,name:"Moen Wood Bench",cat:"Cabinets & Counters",unit:"ea",budgetQty:1,budgetPrice:244.99,actualQty:1,actualPrice:105,supplier:"Home Depot" },
      ]},
      { name:"Fixtures", icon:"🚿", wbs:"5", collapsed:true, laborBudget:250, laborActual:250, items:[
        { id:215,catalogId:39,name:"Delta Handheld Shower Head",cat:"Fixtures & Plumbing",unit:"ea",budgetQty:1,budgetPrice:579,actualQty:1,actualPrice:613.74,supplier:"Home Depot" },
        { id:216,catalogId:40,name:"Wall Mount Faucet",cat:"Fixtures & Plumbing",unit:"ea",budgetQty:1,budgetPrice:200,actualQty:1,actualPrice:175.28,supplier:"Home Depot" },
        { id:217,catalogId:47,name:"Bathroom Mirror",cat:"Cabinets & Counters",unit:"ea",budgetQty:1,budgetPrice:185.99,actualQty:1,actualPrice:185.99,supplier:"Home Depot" },
        { id:218,catalogId:46,name:"Vessel Sink",cat:"Cabinets & Counters",unit:"ea",budgetQty:1,budgetPrice:60.80,actualQty:1,actualPrice:60.80,supplier:"Home Depot" },
      ]},
      { name:"Subcontractor", icon:"👷", wbs:"6", collapsed:true, laborBudget:0, laborActual:0, items:[
        { id:219,catalogId:null,name:"Plumbing Sub",cat:"Other",unit:"ea",budgetQty:1,budgetPrice:700,actualQty:0,actualPrice:0,supplier:"Sub" },
        { id:220,catalogId:null,name:"Electrical Sub",cat:"Other",unit:"ea",budgetQty:1,budgetPrice:500,actualQty:0,actualPrice:0,supplier:"Sub" },
      ]},
    ],
  },
  {
    id:3, name:"Karen", customer:"Karen Williams", location:"Virginia Beach", type:"bath",
    laborType:"flat", laborRate:0, laborHours:0, laborFlat:11891,
    originalQuote:15000, changeOrder:0,
    phases:[
      { name:"Subfloor Prep", icon:"📐", wbs:"1", collapsed:true, laborBudget:1500, laborActual:0, items:[
        { id:301,catalogId:24,name:"Subfloor OSB 3/4 inch",cat:"Subfloor & Prep",unit:"sheet",budgetQty:3,budgetPrice:30,actualQty:0,actualPrice:0,supplier:"Home Depot" },
        { id:302,catalogId:29,name:"2x4 Stud",cat:"Lumber & Framing",unit:"ea",budgetQty:10,budgetPrice:3.75,actualQty:0,actualPrice:0,supplier:"Home Depot" },
        { id:303,catalogId:25,name:"Cement Board 1/2 inch",cat:"Subfloor & Prep",unit:"sheet",budgetQty:4,budgetPrice:12,actualQty:0,actualPrice:0,supplier:"Home Depot" },
      ]},
      { name:"Waterproofing", icon:"🛡️", wbs:"2", collapsed:true, laborBudget:2000, laborActual:0, items:[
        { id:304,catalogId:19,name:"RedGard Waterproofing",cat:"Waterproofing",unit:"gal",budgetQty:1,budgetPrice:50,actualQty:0,actualPrice:0,supplier:"Home Depot" },
        { id:305,catalogId:9,name:"Mapei Keraflex Thinset",cat:"Adhesive & Grout",unit:"bag",budgetQty:5,budgetPrice:27,actualQty:0,actualPrice:0,supplier:"Home Depot" },
        { id:306,catalogId:10,name:"Mapei Perma Grout",cat:"Adhesive & Grout",unit:"bag",budgetQty:4,budgetPrice:20,actualQty:0,actualPrice:0,supplier:"Home Depot" },
        { id:307,catalogId:20,name:"Go Board Tile Backer",cat:"Waterproofing",unit:"ea",budgetQty:5,budgetPrice:30,actualQty:0,actualPrice:0,supplier:"Home Depot" },
      ]},
      { name:"Tile Installation", icon:"🏛️", wbs:"3", collapsed:true, laborBudget:5000, laborActual:0, items:[
        { id:308,catalogId:3,name:"San Grigigio Porcelain",cat:"Tile",unit:"ea",budgetQty:11,budgetPrice:36.08,actualQty:0,actualPrice:0,supplier:"The Tile Shop" },
        { id:309,catalogId:8,name:"Marble Threshold",cat:"Tile",unit:"ea",budgetQty:2,budgetPrice:150,actualQty:0,actualPrice:0,supplier:"The Tile Shop" },
      ]},
      { name:"Fixtures", icon:"🚿", wbs:"4", collapsed:true, laborBudget:3391, laborActual:0, items:[
        { id:310,catalogId:39,name:"Handheld Shower Head",cat:"Fixtures & Plumbing",unit:"ea",budgetQty:1,budgetPrice:400,actualQty:0,actualPrice:0,supplier:"Home Depot" },
        { id:311,catalogId:48,name:"Glass Door",cat:"Glass",unit:"ea",budgetQty:1,budgetPrice:850,actualQty:0,actualPrice:0,supplier:"Local Vendor" },
        { id:312,catalogId:49,name:"Knee Wall Glass",cat:"Glass",unit:"ea",budgetQty:1,budgetPrice:200,actualQty:0,actualPrice:0,supplier:"Local Vendor" },
      ]},
    ]
  },
];

// ══════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════
const fmt = n => "$"+Number(n||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtK = n => Math.abs(n) >= 1000 ? "$"+(n/1000).toFixed(1)+"k" : fmt(n);
const lt = (qty,price) => Number(qty||0)*Number(price||0);
const uid = () => Date.now()+Math.floor(Math.random()*1000);

// ══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════
export default function ProjectBudgetBuilder() {
  const [projects, setProjects] = useState(initProjects);
  const [activeId, setActiveId] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(null);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogCat, setCatalogCat] = useState("All");
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [newProjectModal, setNewProjectModal] = useState(false);
  const [npForm, setNpForm] = useState({name:"",customer:"",location:"",type:"bath",originalQuote:"",laborType:"hourly",laborRate:"",laborHours:"",laborFlat:""});
  const [dbLoaded, setDbLoaded] = useState(false);
  const [budgetFilter, setBudgetFilter] = useState("active");
  const [budgetSearch, setBudgetSearch] = useState("");
  const [seedModal, setSeedModal] = useState(false);
  const [seedableEstimates, setSeedableEstimates] = useState([]);
  const [totalEstimateCount, setTotalEstimateCount] = useState(0);
  const [seedLoading, setSeedLoading] = useState(false);
  const [estimateDrift, setEstimateDrift] = useState(null); // { estimate, added: [], removed: [], estTotal }
  const [installers, setInstallers] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // project id to confirm delete
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const saveTimer = useRef(null);

  // ── Fetch installers from profiles ──
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.rpc('get_login_profiles');
        if (data) setInstallers(data.filter(u => u.active !== false && (u.role === 'installer' || u.role === 'admin' || u.role === 'owner')));
      } catch (err) { console.warn('[Budgets] Installer fetch:', err); }
    })();
  }, []);

  // ── Load materials catalog from Supabase (live) ──
  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      console.log('[BudgetBuilder] Loading catalog from materials_catalog table...');
      const { data, error } = await supabase.from('materials_catalog').select('*').order('name');
      if (error) {
        console.error('[BudgetBuilder] Catalog load error:', error.message, error.details, error.hint);
        return;
      }
      if (data && data.length > 0) {
        const mapped = data.map(r => ({
          id: r.id,
          name: r.name || '',
          cat: r.cat || 'Other',
          supplier: r.supplier || '',
          unit: r.unit || 'ea',
          price: Number(r.last_price) || 0,
          upcharge: Number(r.upcharge) || 0,
        }));
        console.log('[BudgetBuilder] Loaded', mapped.length, 'catalog items');
        setCatalogItems(mapped);
      } else {
        console.warn('[BudgetBuilder] materials_catalog table is empty. Visit the Materials Catalog page to seed.');
        setCatalogItems([]);
      }
    } catch (err) {
      console.error('[BudgetBuilder] Catalog load exception:', err);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  // Load catalog on mount so first open of the picker is instant
  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  // ── Fetch budgets from Supabase on mount ──
  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase.from('project_budgets').select('*').order('created_at', { ascending: false });
        if (error) { console.error('[Budgets] Fetch error:', error.message); return; }
        if (data && data.length > 0) {
          const mapped = data.map(row => ({
            id: row.id,
            name: row.name || "Untitled",
            customer: row.customer || "",
            location: row.location || "",
            type: row.type || "custom",
            budget_status: row.budget_status || row.status || "active",
            laborType: row.labor_type || "flat",
            laborRate: Number(row.labor_rate) || 0,
            laborHours: Number(row.labor_hours) || 0,
            laborFlat: Number(row.labor_flat) || 0,
            originalQuote: Number(row.original_quote) || 0,
            changeOrder: Number(row.change_order) || 0,
            phases: row.phases || [],
            job_id: row.job_id || null,
            estimate_id: row.estimate_id || null,
            seeded_from_estimate: row.seeded_from_estimate || false,
          }));
          setProjects(mapped);
          setActiveId(mapped[0].id);
        } else {
          setActiveId(initProjects[0]?.id || null);
        }
      } catch (err) {
        console.error('[Budgets] Fetch exception:', err);
        setActiveId(initProjects[0]?.id || null);
      }
      setDbLoaded(true);
    })();
  }, []);

  // ── Debounced persist to Supabase on project changes ──
  useEffect(() => {
    if (!dbLoaded) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      for (const p of projects) {
        const row = {
          id: p.id,
          name: p.name,
          customer: p.customer,
          location: p.location || "",
          type: p.type || "custom",
          budget_status: p.budget_status || "planning",
          labor_type: p.laborType || "flat",
          labor_rate: Number(p.laborRate) || 0,
          labor_hours: Number(p.laborHours) || 0,
          labor_flat: Number(p.laborFlat) || 0,
          original_quote: Number(p.originalQuote) || 0,
          change_order: Number(p.changeOrder) || 0,
          phases: p.phases,
          job_id: p.job_id || null,
          estimate_id: p.estimate_id || null,
          seeded_from_estimate: p.seeded_from_estimate || false,
        };
        const { error } = await supabase.from('project_budgets').upsert(row, { onConflict: 'id' });
        if (error) console.error('[Budgets] Save error for', p.name, error.message);
      }
    }, 2000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [projects, dbLoaded]);

  const proj = projects.find(p=>p.id===activeId);
  const setProj = useCallback((fn) => {
    setProjects(ps => ps.map(p => p.id===activeId ? (typeof fn==="function" ? fn(p) : {...p,...fn}) : p));
  },[activeId]);

  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(null),3000); };

  // ── Computed totals ──
  const totals = useMemo(() => {
    if (!proj) return {};
    let budgetTotal=0, actualTotal=0, laborBudgetTotal=0, laborActualTotal=0;
    const byCat={}, byPhase=[];
    proj.phases.forEach(phase => {
      let pBudget=0, pActual=0;
      const pLaborBudget = Number(phase.laborBudget||0);
      const pLaborActual = Number(phase.laborActual||0);
      laborBudgetTotal += pLaborBudget;
      laborActualTotal += pLaborActual;
      phase.items.forEach(item => {
        const b = lt(item.budgetQty,item.budgetPrice);
        const a = lt(item.actualQty,item.actualPrice);
        budgetTotal+=b; actualTotal+=a; pBudget+=b; pActual+=a;
        if(!byCat[item.cat]) byCat[item.cat]={budget:0,actual:0};
        byCat[item.cat].budget+=b; byCat[item.cat].actual+=a;
      });
      byPhase.push({name:phase.name,icon:phase.icon,budget:pBudget,actual:pActual,laborBudget:pLaborBudget,laborActual:pLaborActual,count:phase.items.length});
    });
    // Use phase-level labor if any phase has labor, otherwise fall back to project-level
    const hasPhaseLabor = laborBudgetTotal > 0 || laborActualTotal > 0;
    const laborCost = hasPhaseLabor ? laborActualTotal : (proj.laborType==="flat" ? (proj.laborFlat||0) : (proj.laborRate||0)*(proj.laborHours||0));
    const laborBudget = hasPhaseLabor ? laborBudgetTotal : laborCost;
    const quoteTotal = (proj.originalQuote||0)+(proj.changeOrder||0);
    const grossProfit = quoteTotal - actualTotal - laborCost;
    const margin = quoteTotal>0 ? Math.round(grossProfit/quoteTotal*100) : 0;
    return { budgetTotal, actualTotal, laborCost, laborBudget, laborBudgetTotal, laborActualTotal, hasPhaseLabor, quoteTotal, grossProfit, margin, byCat, byPhase, variance:budgetTotal-actualTotal };
  },[proj]);

  // ── Phase toggle ──
  const togglePhase = (idx) => {
    setProj(p => ({...p, phases:p.phases.map((ph,i)=>i===idx?{...ph,collapsed:!ph.collapsed}:ph)}));
  };

  // ── Inline edit ──
  const updateItem = (phaseIdx, itemId, field, value) => {
    setProj(p => ({...p,phases:p.phases.map((ph,pi)=>pi===phaseIdx?{...ph,items:ph.items.map(it=>it.id===itemId?{...it,[field]:value}:it)}:ph)}));
  };

  // ── Add from catalog ──
  const addFromCatalog = (catItem, phaseIdx) => {
    const newItem = {
      id:uid(), catalogId:catItem.id, name:catItem.name, cat:catItem.cat, unit:catItem.unit,
      budgetQty:1, budgetPrice:catItem.price*(1+catItem.upcharge/100),
      actualQty:0, actualPrice:0, supplier:catItem.supplier,
    };
    setProj(p => ({...p,phases:p.phases.map((ph,i)=>i===phaseIdx?{...ph,items:[...ph.items,newItem],collapsed:false}:ph)}));
    setCatalogOpen(null);
    showToast(`"${catItem.name}" added to ${proj.phases[phaseIdx].name}`);
  };

  // ── Add manual item ──
  const addManualItem = (phaseIdx) => {
    const newItem = { id:uid(),catalogId:null,name:"New Item",cat:"Other",unit:"ea",budgetQty:1,budgetPrice:0,actualQty:0,actualPrice:0,supplier:"" };
    setProj(p => ({...p,phases:p.phases.map((ph,i)=>i===phaseIdx?{...ph,items:[...ph.items,newItem],collapsed:false}:ph)}));
    showToast("Manual item added — click to edit");
  };

  const deleteItem = (phaseIdx, itemId) => {
    setProj(p => ({...p,phases:p.phases.map((ph,i)=>i===phaseIdx?{...ph,items:ph.items.filter(it=>it.id!==itemId)}:ph)}));
  };

  // ── Update phase labor ──
  const updatePhaseLabor = (phaseIdx, field, value) => {
    setProj(p => ({...p,phases:p.phases.map((ph,i)=>i===phaseIdx?{...ph,[field]:Number(value)||0}:ph)}));
  };

  const addLaborBreakdown = (phaseIdx) => {
    setProj(p => ({...p,phases:p.phases.map((ph,i)=>{
      if(i!==phaseIdx) return ph;
      const bd = [...(ph.laborBreakdown||[]), { installer:"", amount:0 }];
      return {...ph, laborBreakdown: bd};
    })}));
  };

  const updateLaborBreakdown = (phaseIdx, bdIdx, field, value) => {
    setProj(p => ({...p,phases:p.phases.map((ph,i)=>{
      if(i!==phaseIdx) return ph;
      const bd = (ph.laborBreakdown||[]).map((row,j)=>j===bdIdx?{...row,[field]:field==="amount"?(Number(value)||0):value}:row);
      const total = bd.reduce((s,r)=>s+(Number(r.amount)||0),0);
      return {...ph, laborBreakdown: bd, laborActual: total};
    })}));
  };

  const removeLaborBreakdown = (phaseIdx, bdIdx) => {
    setProj(p => ({...p,phases:p.phases.map((ph,i)=>{
      if(i!==phaseIdx) return ph;
      const bd = (ph.laborBreakdown||[]).filter((_,j)=>j!==bdIdx);
      const total = bd.reduce((s,r)=>s+(Number(r.amount)||0),0);
      return {...ph, laborBreakdown: bd, laborActual: bd.length>0?total:ph.laborActual};
    })}));
  };

  // ── Add phase ──
  const addPhase = () => {
    setProj(p => ({...p,phases:[...p.phases,{name:"New Phase",icon:"📦",wbs:String(p.phases.length+1),collapsed:false,laborBudget:0,laborActual:0,items:[]}]}));
  };

  // ── New project ──
  const createProject = () => {
    const tpl = PHASE_TEMPLATES[npForm.type] || PHASE_TEMPLATES.custom;
    const np = {
      id:uid(), name:npForm.name||"New Project", customer:npForm.customer, location:npForm.location, type:npForm.type,
      budget_status:"planning",
      laborType:npForm.laborType||"hourly", laborRate:Number(npForm.laborRate)||0, laborHours:Number(npForm.laborHours)||0, laborFlat:Number(npForm.laborFlat)||0,
      originalQuote:Number(npForm.originalQuote)||0, changeOrder:0,
      phases:tpl.map(t=>({...t,collapsed:false,laborBudget:0,laborActual:0,items:[]})),
    };
    setProjects(p=>[...p,np]);
    setActiveId(np.id);
    setNewProjectModal(false);
    showToast(`"${np.name}" project created`);
  };

  // ── Delete project ──
  const deleteProject = async (projId) => {
    try {
      await supabase.from('project_budgets').delete().eq('id', projId);
    } catch (err) { console.warn('[Budgets] Delete error:', err); }
    setProjects(ps => ps.filter(p => p.id !== projId));
    if (activeId === projId) setActiveId(null);
    setDeleteConfirm(null);
    showToast("Project budget deleted");
  };

  // ── Bulk actions ──
  const bulkArchive = async () => {
    const ids = [...bulkSelected];
    setProjects(ps => ps.map(p => ids.includes(p.id) ? { ...p, budget_status: "archived" } : p));
    for (const id of ids) {
      try { await supabase.from('project_budgets').update({ budget_status: "archived" }).eq('id', id); } catch {}
    }
    setBulkSelected(new Set());
    setBulkMode(false);
    showToast(`${ids.length} project(s) archived`);
  };

  const bulkDelete = async () => {
    const ids = [...bulkSelected];
    for (const id of ids) {
      try { await supabase.from('project_budgets').delete().eq('id', id); } catch {}
    }
    setProjects(ps => ps.filter(p => !ids.includes(p.id)));
    if (ids.includes(activeId)) setActiveId(null);
    setBulkSelected(new Set());
    setBulkMode(false);
    setDeleteConfirm(null);
    showToast(`${ids.length} project(s) deleted`);
  };

  const toggleBulkSelect = (id) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Seed from Estimate ──
  // Fetches ALL estimates (not just those linked to jobs) and filters out ones that
  // already have a project budget. Customer address is looked up from the customers
  // table by name match (same pattern used elsewhere in the CRM).
  const openSeedModal = async () => {
    setSeedModal(true);
    setSeedLoading(true);
    try {
      const [estRes, custRes] = await Promise.all([
        supabase.from('estimates').select('*').order('id', { ascending: false }),
        supabase.from('customers').select('id, name, address'),
      ]);
      const estimates = estRes.data || [];
      const customers = custRes.data || [];
      setTotalEstimateCount(estimates.length);
      const existingEstIds = new Set(projects.filter(p => p.estimate_id).map(p => p.estimate_id));
      const seedable = [];
      for (const est of estimates) {
        if (!est.id) continue;
        if (existingEstIds.has(est.id)) continue;
        const total = Number(est.override_total) || (est.items || []).reduce(
          (s, i) => s + (Number(i.qty) * Number(i.price)) + (i.materials || []).reduce(
            (ms, m) => ms + (Number(m.qty) * Number(m.price)), 0), 0);
        const matCount = (est.items || []).reduce((s, i) => s + (i.materials || []).length, 0);
        const cust = customers.find(c => (c.name || '').toLowerCase() === (est.customer || '').toLowerCase());
        seedable.push({ est, total, matCount, address: cust?.address || "" });
      }
      setSeedableEstimates(seedable);
    } catch (err) { console.error('[Seed] Error fetching:', err); }
    setSeedLoading(false);
  };

  const phaseIcon = (desc) => {
    const d = (desc || "").toLowerCase();
    if (/demo|demolition|tear/.test(d)) return "🔨";
    if (/subfloor|prep|level/.test(d)) return "📐";
    if (/waterproof|membrane|kerdi/.test(d)) return "🛡️";
    if (/tile|porcelain|mosaic|stone|marble/.test(d)) return "🏛️";
    if (/fixture|plumbing|faucet|shower/.test(d)) return "🚿";
    if (/cabinet|counter|vanity/.test(d)) return "🗄️";
    if (/electric|lighting/.test(d)) return "💡";
    if (/labor|install/.test(d)) return "👷";
    if (/glass|mirror/.test(d)) return "🪟";
    if (/paint|finish/.test(d)) return "🎨";
    return "📦";
  };

  const seedFromEstimate = async (item) => {
    const { est, total, address } = item;
    const phases = (est.items || []).map((scope, idx) => {
      const hasMaterials = (scope.materials || []).length > 0;
      const lineItems = (scope.materials || []).map((mat, mi) => ({
        id: uid() + idx * 100 + mi + 10,
        catalogId: null, name: mat.desc || "Unnamed material", cat: "Other",
        unit: mat.unit || "ea", budgetQty: Number(mat.qty) || 0, budgetPrice: Number(mat.price) || 0,
        actualQty: 0, actualPrice: 0, supplier: "", source: "estimate",
      }));
      const scopeValue = Number(scope.qty || 0) * Number(scope.price || 0);
      return {
        name: scope.desc || `Phase ${idx + 1}`, icon: phaseIcon(scope.desc),
        wbs: String(idx + 1), collapsed: false,
        laborBudget: hasMaterials ? 0 : scopeValue, laborActual: 0, items: lineItems,
      };
    });
    const np = {
      id: uid(), name: est.project || "Untitled", customer: est.customer || "",
      location: address || "", type: "custom", budget_status: "active",
      laborType: "flat", laborRate: 0, laborHours: 0, laborFlat: 0,
      originalQuote: total, changeOrder: 0, phases,
      job_id: null, estimate_id: est.id, seeded_from_estimate: true,
    };
    setProjects(p => [...p, np]);
    setActiveId(np.id);
    setBudgetFilter("active");
    setSeedModal(false);
    showToast(`Budget seeded from ${est.id} · ${est.project}`);
  };

  // ── Estimate drift detection (for seeded budgets) ──
  // Compares the live estimate to the current budget and returns lists of items
  // added to / removed from the estimate since seeding. Matching is by scope
  // description (case-insensitive, trimmed) and material name within scope.
  // Only items with source='estimate' are considered when checking removals so
  // manually-added budget items are never flagged.
  const computeEstimateDrift = (budget, estimate) => {
    const added = [];
    const removed = [];
    const estScopes = estimate.items || [];
    const normKey = (s) => (s || '').toLowerCase().trim();

    const budgetScopeMap = new Map();
    (budget.phases || []).forEach(p => { budgetScopeMap.set(normKey(p.name), p); });
    const estScopeMap = new Map();
    estScopes.forEach(s => { estScopeMap.set(normKey(s.desc), s); });

    // Additions: scopes/materials in estimate but not budget
    for (const s of estScopes) {
      const key = normKey(s.desc);
      if (!key) continue;
      const phase = budgetScopeMap.get(key);
      if (!phase) {
        added.push({ type: 'scope', scope: s.desc });
      } else {
        const phaseMats = new Set((phase.items || []).map(it => normKey(it.name)));
        for (const mat of (s.materials || [])) {
          const mkey = normKey(mat.desc);
          if (!mkey) continue;
          if (!phaseMats.has(mkey)) {
            added.push({ type: 'material', scope: s.desc, material: mat.desc });
          }
        }
      }
    }

    // Removals: scopes/materials in budget but not estimate (flag-only, never auto-delete)
    for (const phase of (budget.phases || [])) {
      const key = normKey(phase.name);
      if (!key) continue;
      const estScope = estScopeMap.get(key);
      if (!estScope) {
        removed.push({ type: 'scope', scope: phase.name });
      } else {
        const estMats = new Set((estScope.materials || []).map(m => normKey(m.desc)));
        for (const it of (phase.items || [])) {
          if (it.source !== 'estimate') continue; // skip manually-added items
          const mkey = normKey(it.name);
          if (!mkey) continue;
          if (!estMats.has(mkey)) {
            removed.push({ type: 'material', scope: phase.name, material: it.name });
          }
        }
      }
    }

    const estTotal = Number(estimate.override_total) || estScopes.reduce(
      (s, i) => s + (Number(i.qty) * Number(i.price)) + (i.materials || []).reduce(
        (ms, m) => ms + (Number(m.qty) * Number(m.price)), 0), 0);

    return { added, removed, estTotal };
  };

  // Auto-detect drift whenever the active budget changes or its linked estimate updates
  useEffect(() => {
    if (!proj?.estimate_id) { setEstimateDrift(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.from('estimates').select('*').eq('id', proj.estimate_id).maybeSingle();
        if (cancelled) return;
        if (error || !data) { setEstimateDrift(null); return; }
        const drift = computeEstimateDrift(proj, data);
        setEstimateDrift({ estimate: data, ...drift });
      } catch (e) { console.warn('[Budgets] drift check:', e); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, proj?.estimate_id, proj?.phases?.length]);

  // Merge NEW estimate items into the active budget. Preserves all existing
  // phases, line items, actuals, suppliers, and manual additions. Also updates
  // originalQuote to match the current estimate total. Never deletes anything.
  const syncFromEstimate = () => {
    if (!estimateDrift || !proj) return;
    const { estimate, added, estTotal } = estimateDrift;
    if (added.length === 0 && Number(estTotal) === Number(proj.originalQuote)) {
      showToast("Budget already up to date");
      return;
    }
    const estScopes = estimate.items || [];
    const normKey = (s) => (s || '').toLowerCase().trim();

    setProjects(ps => ps.map(p => {
      if (p.id !== activeId) return p;
      const newPhases = [...(p.phases || [])];

      for (const a of added) {
        if (a.type === 'scope') {
          const estScope = estScopes.find(s => normKey(s.desc) === normKey(a.scope));
          if (!estScope) continue;
          const hasMaterials = (estScope.materials || []).length > 0;
          const lineItems = (estScope.materials || []).map((mat, mi) => ({
            id: uid() + mi + 1,
            catalogId: null, name: mat.desc || "Unnamed material", cat: "Other",
            unit: mat.unit || "ea", budgetQty: Number(mat.qty) || 0, budgetPrice: Number(mat.price) || 0,
            actualQty: 0, actualPrice: 0, supplier: "", source: "estimate",
          }));
          const scopeValue = Number(estScope.qty || 0) * Number(estScope.price || 0);
          newPhases.push({
            name: estScope.desc || "New scope", icon: phaseIcon(estScope.desc),
            wbs: String(newPhases.length + 1), collapsed: false,
            laborBudget: hasMaterials ? 0 : scopeValue, laborActual: 0, items: lineItems,
          });
        } else if (a.type === 'material') {
          const phaseIdx = newPhases.findIndex(ph => normKey(ph.name) === normKey(a.scope));
          if (phaseIdx < 0) continue;
          const estScope = estScopes.find(s => normKey(s.desc) === normKey(a.scope));
          const mat = (estScope?.materials || []).find(m => normKey(m.desc) === normKey(a.material));
          if (!mat) continue;
          const existing = new Set((newPhases[phaseIdx].items || []).map(it => normKey(it.name)));
          if (existing.has(normKey(mat.desc))) continue; // safety check
          newPhases[phaseIdx] = {
            ...newPhases[phaseIdx],
            items: [...(newPhases[phaseIdx].items || []), {
              id: uid(),
              catalogId: null, name: mat.desc || "Unnamed material", cat: "Other",
              unit: mat.unit || "ea", budgetQty: Number(mat.qty) || 0, budgetPrice: Number(mat.price) || 0,
              actualQty: 0, actualPrice: 0, supplier: "", source: "estimate",
            }],
          };
        }
      }

      return { ...p, phases: newPhases, originalQuote: Number(estTotal) || p.originalQuote };
    }));

    const parts = [];
    if (added.length > 0) parts.push(`${added.length} new item${added.length === 1 ? '' : 's'}`);
    if (Number(estTotal) !== Number(proj.originalQuote)) parts.push(`quote updated to ${fmt(estTotal)}`);
    showToast(`Synced from estimate · ${parts.join(' · ')}`);
    // Clear drift so the banner disappears until the next estimate change
    setEstimateDrift(prev => prev ? { ...prev, added: [] } : null);
  };

  if (!dbLoaded) return <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"var(--font-body)"}}><div style={{textAlign:"center"}}><div style={{fontSize:32,marginBottom:8}}>📊</div><div style={{fontSize:16,color:"var(--stone-500)"}}>Loading project budgets…</div></div></div>;

  return (
    <div className="tile-bg" style={{minHeight:"100vh",padding:28,fontFamily:"var(--font-body)"}}>
      <style>{CSS}</style>

      {/* ════ HEADER ════ */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <h1 style={{fontFamily:"var(--font-display)",fontSize:32,fontWeight:600,color:"var(--stone-900)",margin:0}}>Project Budget Builder</h1>
          <p style={{fontSize:14,color:"var(--stone-500)",marginTop:4}}>Construction budget by phase — replaces your Excel templates</p>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={openSeedModal}
            style={{...btnPrimary,background:"var(--stone-800)"}}>🌱 Seed from Estimate</button>
          <button onClick={()=>{setNpForm({name:"",customer:"",location:"",type:"bath",originalQuote:"",laborType:"hourly",laborRate:"",laborHours:"",laborFlat:""});setNewProjectModal(true)}}
            style={{...btnPrimary}}>＋ New Project Budget</button>
        </div>
      </div>

      {/* ════ FILTER & SEARCH ════ */}
      <div style={{display:"flex",gap:12,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        <div style={{display:"flex",gap:0,background:"var(--stone-200)",borderRadius:"var(--radius)",padding:3}}>
          {[["active","Active"],["committed","Committed"],["planning","Planning"],["archived","Archived"],["all","All"]].map(([k,l])=>{
            const ct = k==="all" ? projects.length : projects.filter(p=>(p.budget_status||"active")===k).length;
            return (
              <button key={k} onClick={()=>setBudgetFilter(k)}
                style={{padding:"5px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",fontFamily:"var(--font-body)",
                  background:budgetFilter===k?"white":"transparent",color:budgetFilter===k?"var(--stone-900)":"var(--stone-500)",
                  boxShadow:budgetFilter===k?"0 1px 3px rgba(0,0,0,.1)":"none",transition:"all .2s",whiteSpace:"nowrap"}}>
                {l} {ct>0?`(${ct})`:""}
              </button>
            );
          })}
        </div>
        <div style={{position:"relative",flex:"1 1 200px",maxWidth:300}}>
          <input value={budgetSearch} onChange={e=>setBudgetSearch(e.target.value)} placeholder="Search projects…"
            style={{width:"100%",padding:"7px 12px 7px 32px",fontSize:12,borderRadius:"var(--radius)",border:"1.5px solid var(--stone-200)"}} />
          <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",fontSize:13,opacity:.4}}>🔍</span>
        </div>
        <div style={{display:"flex",gap:6,marginLeft:"auto"}}>
          <button onClick={()=>{setBulkMode(!bulkMode);setBulkSelected(new Set())}}
            style={{padding:"5px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:`1.5px solid ${bulkMode?"var(--accent-red)":"var(--stone-300)"}`,
              background:bulkMode?"var(--accent-red)":"white",color:bulkMode?"white":"var(--stone-600)",fontFamily:"var(--font-body)",transition:"all .2s"}}>
            {bulkMode ? "✕ Cancel" : "☑ Select"}
          </button>
        </div>
      </div>

      {/* ════ BULK ACTIONS BAR ════ */}
      {bulkMode && bulkSelected.size > 0 && (
        <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:12,padding:"8px 14px",background:"var(--stone-200)",borderRadius:"var(--radius)",animation:"fadeIn .2s ease"}}>
          <span style={{fontSize:12,fontWeight:600,color:"var(--stone-700)"}}>{bulkSelected.size} selected</span>
          <button onClick={bulkArchive}
            style={{padding:"4px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:"1px solid var(--stone-400)",background:"white",color:"var(--stone-700)",fontFamily:"var(--font-body)"}}>
            ⊘ Archive
          </button>
          <button onClick={()=>setDeleteConfirm("bulk")}
            style={{padding:"4px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:"1px solid var(--accent-red)",background:"white",color:"var(--accent-red)",fontFamily:"var(--font-body)"}}>
            🗑 Delete
          </button>
          <button onClick={()=>{const filtered=projects.filter(p=>{const status=p.budget_status||"active";if(budgetFilter!=="all"&&status!==budgetFilter)return false;if(budgetSearch){const s=budgetSearch.toLowerCase();return(p.name||"").toLowerCase().includes(s)||(p.customer||"").toLowerCase().includes(s)}return true});setBulkSelected(new Set(filtered.map(p=>p.id)))}}
            style={{padding:"4px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:"1px solid var(--stone-300)",background:"white",color:"var(--stone-600)",fontFamily:"var(--font-body)",marginLeft:"auto"}}>
            Select All Visible
          </button>
        </div>
      )}

      {/* ════ PROJECT LIST (compact table) ════ */}
      {(() => {
        const filtered = projects.filter(p => {
          const status = p.budget_status || "active";
          if (budgetFilter !== "all" && status !== budgetFilter) return false;
          if (budgetSearch) {
            const s = budgetSearch.toLowerCase();
            return (p.name||"").toLowerCase().includes(s) || (p.customer||"").toLowerCase().includes(s) || (p.location||"").toLowerCase().includes(s);
          }
          return true;
        });
        const statusColor = (s) => s==="active"?"var(--accent-green)":s==="committed"?"var(--accent-blue)":s==="archived"?"var(--stone-400)":"var(--accent-amber)";
        const statusIcon = (s) => s==="active"?"●":s==="committed"?"◉":s==="archived"?"⊘":"◯";
        return (
          <div style={{background:"white",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",marginBottom:20,overflow:"hidden",boxShadow:"var(--shadow-sm)"}}>
            {/* Table header */}
            <div style={{display:"grid",gridTemplateColumns:bulkMode?"32px 1fr 160px 120px 100px 110px 40px":"1fr 160px 120px 100px 110px 40px",
              padding:"8px 14px",background:"var(--stone-100)",borderBottom:"1px solid var(--stone-200)",fontSize:10,fontWeight:700,
              color:"var(--stone-500)",textTransform:"uppercase",letterSpacing:".04em",gap:8,alignItems:"center"}}>
              {bulkMode && <span/>}
              <span>Project</span>
              <span>Customer</span>
              <span>Status</span>
              <span style={{textAlign:"right"}}>Spent</span>
              <span style={{textAlign:"right"}}>Variance</span>
              <span/>
            </div>
            {/* Rows */}
            {filtered.length === 0 && (
              <div style={{padding:"24px 14px",textAlign:"center",fontSize:13,color:"var(--stone-400)"}}>No projects match this filter.</div>
            )}
            {filtered.map(p => {
              const sel = p.id===activeId;
              let bTotal=0,aTotal=0,lbTotal=0,laTotal=0;
              p.phases.forEach(ph=>{ph.items.forEach(it=>{bTotal+=lt(it.budgetQty,it.budgetPrice);aTotal+=lt(it.actualQty,it.actualPrice)});lbTotal+=Number(ph.laborBudget||0);laTotal+=Number(ph.laborActual||0)});
              const totalBudget = bTotal+lbTotal;
              const totalActual = aTotal+laTotal;
              const over = totalBudget>0 && totalActual>totalBudget;
              const st = p.budget_status||"active";
              const checked = bulkSelected.has(p.id);
              return (
                <div key={p.id}
                  onClick={()=>{ if(bulkMode){toggleBulkSelect(p.id)} else {setActiveId(p.id)} }}
                  style={{display:"grid",gridTemplateColumns:bulkMode?"32px 1fr 160px 120px 100px 110px 40px":"1fr 160px 120px 100px 110px 40px",
                    padding:"10px 14px",borderBottom:"1px solid var(--stone-100)",cursor:"pointer",gap:8,alignItems:"center",
                    background:sel?"var(--stone-900)":checked?"rgba(201,169,110,.08)":"white",
                    color:sel?"white":"var(--stone-700)",transition:"background .15s",fontSize:13}}>
                  {bulkMode && (
                    <div style={{display:"flex",justifyContent:"center"}}>
                      <div style={{width:16,height:16,borderRadius:3,border:`1.5px solid ${checked?"var(--warm-gold)":"var(--stone-300)"}`,
                        background:checked?"var(--warm-gold)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s"}}>
                        {checked && <span style={{color:"white",fontSize:11,fontWeight:700}}>✓</span>}
                      </div>
                    </div>
                  )}
                  <div>
                    <div style={{fontWeight:600,fontSize:13,lineHeight:1.2}}>{p.name}</div>
                    {p.location && <div style={{fontSize:10,opacity:.5,marginTop:1}}>📍 {p.location}</div>}
                  </div>
                  <div style={{fontSize:12,opacity:.8}}>{p.customer}</div>
                  <div>
                    <span style={{fontSize:10,fontWeight:700,color:statusColor(st),background:`${statusColor(st)}15`,
                      padding:"2px 8px",borderRadius:99,display:"inline-flex",alignItems:"center",gap:4}}>
                      {statusIcon(st)} {st.charAt(0).toUpperCase()+st.slice(1)}
                    </span>
                  </div>
                  <div style={{textAlign:"right",fontSize:12,fontWeight:600,fontFamily:"var(--font-body)"}}>{fmtK(totalActual)}</div>
                  <div style={{textAlign:"right",fontSize:12,fontWeight:600}}>
                    {totalBudget>0 ? (
                      <span style={{color:sel?(over?"#fca5a5":"#86efac"):(over?"var(--accent-red)":"var(--accent-green)")}}>
                        {over?`▲${Math.round((totalActual/totalBudget-1)*100)}%`:`▼${Math.max(0,Math.round((1-totalActual/totalBudget)*100))}%`}
                      </span>
                    ) : <span style={{opacity:.3}}>—</span>}
                  </div>
                  <div style={{display:"flex",justifyContent:"center"}}>
                    {!bulkMode && (
                      <button onClick={(e)=>{e.stopPropagation();setDeleteConfirm(p.id)}} title="Delete project"
                        style={{padding:4,background:"none",border:"none",cursor:"pointer",opacity:.3,transition:"opacity .2s",fontSize:14,lineHeight:1}}
                        onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=.3}>
                        🗑
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ════ DELETE CONFIRMATION MODAL ════ */}
      {deleteConfirm && (
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setDeleteConfirm(null)}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:"white",borderRadius:"var(--radius-lg)",padding:"28px 32px",maxWidth:420,width:"90%",boxShadow:"var(--shadow-lg)",animation:"modalIn .15s ease"}}>
            <div style={{fontSize:18,fontWeight:700,fontFamily:"var(--font-display)",marginBottom:8,color:"var(--stone-900)"}}>
              {deleteConfirm === "bulk" ? `Delete ${bulkSelected.size} Project${bulkSelected.size>1?"s":""}?` : "Delete Project Budget?"}
            </div>
            <p style={{fontSize:13,color:"var(--stone-600)",marginBottom:20,lineHeight:1.5}}>
              {deleteConfirm === "bulk"
                ? `This will permanently remove ${bulkSelected.size} project budget${bulkSelected.size>1?"s":""} and all associated phase/material data. This cannot be undone.`
                : `This will permanently remove "${(projects.find(p=>p.id===deleteConfirm)||{}).name || "this project"}" and all associated phase/material data. This cannot be undone.`}
            </p>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setDeleteConfirm(null)}
                style={{padding:"8px 20px",borderRadius:6,border:"1px solid var(--stone-300)",background:"white",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"var(--font-body)",color:"var(--stone-600)"}}>
                Cancel
              </button>
              <button onClick={()=>deleteConfirm==="bulk"?bulkDelete():deleteProject(deleteConfirm)}
                style={{padding:"8px 20px",borderRadius:6,border:"none",background:"var(--accent-red)",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"var(--font-body)",color:"white"}}>
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════ ESTIMATE DRIFT BANNER (seeded budgets only) ════ */}
      {proj && proj.estimate_id && estimateDrift && (estimateDrift.added.length > 0 || estimateDrift.removed.length > 0 || Number(estimateDrift.estTotal) !== Number(proj.originalQuote)) && (
        <div style={{background:"var(--warm-gold-light)",border:"1px solid var(--warm-gold)",borderRadius:"var(--radius-lg)",padding:"14px 18px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:16,flexWrap:"wrap",boxShadow:"var(--shadow-sm)"}}>
          <div style={{flex:"1 1 320px",minWidth:0}}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--stone-900)",display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
              <span style={{fontSize:15}}>↻</span>
              Estimate {proj.estimate_id} has changed since this budget was seeded
            </div>
            {estimateDrift.added.length > 0 && (
              <div style={{fontSize:12,color:"var(--stone-800)",marginTop:4}}>
                <strong style={{color:"var(--accent-green)"}}>+{estimateDrift.added.length} new</strong>
                {": "}
                {estimateDrift.added.slice(0,4).map((a,i) => (
                  <span key={i} style={{marginRight:6}}>
                    {a.type === 'scope' ? `📁 ${a.scope}` : `• ${a.material} (in ${a.scope})`}
                    {i < Math.min(estimateDrift.added.length, 4) - 1 ? "," : ""}
                  </span>
                ))}
                {estimateDrift.added.length > 4 && <span style={{color:"var(--stone-600)"}}>+ {estimateDrift.added.length - 4} more</span>}
              </div>
            )}
            {estimateDrift.removed.length > 0 && (
              <div style={{fontSize:12,color:"var(--stone-800)",marginTop:4}}>
                <strong style={{color:"var(--accent-red)"}}>⚠ {estimateDrift.removed.length} removed from estimate</strong>
                {" (still in budget — review and delete manually if needed): "}
                {estimateDrift.removed.slice(0,3).map((r,i) => (
                  <span key={i} style={{marginRight:6}}>
                    {r.type === 'scope' ? `📁 ${r.scope}` : `• ${r.material} (in ${r.scope})`}
                    {i < Math.min(estimateDrift.removed.length, 3) - 1 ? "," : ""}
                  </span>
                ))}
                {estimateDrift.removed.length > 3 && <span style={{color:"var(--stone-600)"}}>+ {estimateDrift.removed.length - 3} more</span>}
              </div>
            )}
            {Number(estimateDrift.estTotal) !== Number(proj.originalQuote) && (
              <div style={{fontSize:12,color:"var(--stone-800)",marginTop:4}}>
                <strong style={{color:"var(--accent-blue)"}}>Contract total:</strong> {fmt(proj.originalQuote)} → {fmt(estimateDrift.estTotal)}
              </div>
            )}
          </div>
          <div style={{display:"flex",gap:8,flexShrink:0}}>
            {estimateDrift.added.length > 0 || Number(estimateDrift.estTotal) !== Number(proj.originalQuote) ? (
              <button onClick={syncFromEstimate}
                style={{...btnPrimary,background:"var(--stone-800)",padding:"8px 16px",fontSize:12}}>
                ↻ Sync now
              </button>
            ) : null}
            <button onClick={() => setEstimateDrift(null)}
              style={{...btnSmGhost,background:"rgba(255,255,255,.5)"}}>Dismiss</button>
          </div>
        </div>
      )}

      {/* ════ PROJECT SUMMARY CARD (mirrors Excel bottom section) ════ */}
      {!proj && projects.length > 0 && (
        <div style={{textAlign:"center",padding:"40px 20px",color:"var(--stone-400)",fontSize:14}}>
          ← Select a project from the list above to view its budget
        </div>
      )}
      {proj && (<>
      <div style={{background:"var(--stone-800)",borderRadius:"var(--radius-lg)",padding:"20px 24px",marginBottom:20,color:"white",boxShadow:"var(--shadow-md)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
          <div>
            <div style={{fontFamily:"var(--font-display)",fontSize:22,fontWeight:700}}>{proj.name} — {proj.customer}</div>
            <div style={{fontSize:12,opacity:.6,marginTop:2,display:"flex",alignItems:"center",gap:8}}>
              📍 {proj.location} · {proj.type==="bath"?"Bathroom":"Kitchen"} Project
              <select value={proj.budget_status||"planning"} onChange={e=>setProj(p=>({...p,budget_status:e.target.value}))}
                style={{background:"rgba(255,255,255,.15)",color:"white",border:"1px solid rgba(255,255,255,.25)",borderRadius:99,
                  fontSize:11,fontWeight:700,padding:"4px 10px",cursor:"pointer",appearance:"auto",textTransform:"uppercase",letterSpacing:".03em"}}>
                <option value="planning" style={{background:"#f59e0b",color:"white",fontWeight:700,padding:"6px"}}>◯ Planning</option>
                <option value="committed" style={{background:"#3b82f6",color:"white",fontWeight:700,padding:"6px"}}>◉ Committed</option>
                <option value="active" style={{background:"#22c55e",color:"white",fontWeight:700,padding:"6px"}}>● Active</option>
                <option value="archived" style={{background:"#6b7280",color:"white",fontWeight:700,padding:"6px"}}>⊘ Archived</option>
              </select>
            </div>
          </div>
          <div style={{display:"flex",gap:6}}>
            <span style={{background:"rgba(201,169,110,.2)",color:"var(--warm-gold)",padding:"4px 12px",borderRadius:99,fontSize:11,fontWeight:700}}>
              Original Quote: {fmt(proj.originalQuote)}
            </span>
            {proj.changeOrder>0 && <span style={{background:"rgba(196,149,74,.2)",color:"#f0c674",padding:"4px 12px",borderRadius:99,fontSize:11,fontWeight:700}}>
              Change Order: {fmt(proj.changeOrder)}
            </span>}
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:12}}>
          {[
            {label:"Material Budget",value:fmtK(totals.budgetTotal),color:"var(--accent-blue)"},
            {label:"Actual Spend",value:fmtK(totals.actualTotal),color:totals.actualTotal>totals.budgetTotal?"var(--accent-red)":"var(--accent-green)"},
            {label:"Variance",value:(totals.variance>=0?"+":"")+fmtK(totals.variance),color:totals.variance>=0?"var(--accent-green)":"var(--accent-red)"},
            {label:"My Labor",value:fmt(totals.laborCost),color:"var(--warm-gold-light)",sub:totals.hasPhaseLabor?`Budget: ${fmt(totals.laborBudget)} · ${proj.phases.filter(p=>(p.laborBudget||0)>0||(p.laborActual||0)>0).length} phases`:(proj.laborType==="flat"?"Flat rate":`${proj.laborHours||0}h × ${fmt(proj.laborRate||0)}/hr`)},
            {label:"Project Total",value:fmtK(totals.quoteTotal),color:"white"},
            {label:"Gross Profit",value:fmtK(totals.grossProfit),color:totals.grossProfit>0?"var(--accent-green)":"var(--accent-red)"},
            {label:"Margin",value:totals.margin+"%",color:totals.margin>40?"var(--accent-green)":totals.margin>20?"var(--accent-amber)":"var(--accent-red)"},
          ].map((s,i) => (
            <div key={i} style={{background:"rgba(255,255,255,.06)",borderRadius:"var(--radius)",padding:"12px 14px"}}>
              <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:".05em",opacity:.5,marginBottom:4}}>{s.label}</div>
              <div style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700,color:s.color}}>{s.value}</div>
              {s.sub && <div style={{fontSize:10,opacity:.4,marginTop:2}}>{s.sub}</div>}
            </div>
          ))}
        </div>

        {/* Budget progress bar */}
        {totals.budgetTotal > 0 && (() => {
          const pct = Math.min(Math.round(totals.actualTotal/totals.budgetTotal*100),100);
          const over = totals.actualTotal>totals.budgetTotal;
          const barColor = over?"var(--accent-red)":pct>80?"var(--accent-amber)":"var(--accent-green)";
          return (
            <div style={{marginTop:16}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,opacity:.6,marginBottom:4}}>
                <span>{pct}% of material budget used</span>
                <span style={{color:barColor,fontWeight:700}}>{over?"⚠️ Over budget":"✓ On track"}</span>
              </div>
              <div style={{height:8,background:"rgba(255,255,255,.1)",borderRadius:99,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${pct}%`,background:barColor,borderRadius:99,transition:"width .5s ease"}}/>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ════ PHASE-BY-PHASE BREAKDOWN (mirrors Excel WBS sections) ════ */}
      {proj.phases.map((phase,phaseIdx) => {
        const pBudget = phase.items.reduce((s,it)=>s+lt(it.budgetQty,it.budgetPrice),0);
        const pActual = phase.items.reduce((s,it)=>s+lt(it.actualQty,it.actualPrice),0);
        const pLaborBudget = Number(phase.laborBudget||0);
        const pLaborActual = Number(phase.laborActual||0);
        const pTotalBudget = pBudget + pLaborBudget;
        const pTotalActual = pActual + pLaborActual;
        const pVariance = pTotalBudget - pTotalActual;
        const over = pTotalBudget>0 && pTotalActual>pTotalBudget;

        return (
          <div key={phaseIdx} style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",boxShadow:"var(--shadow-sm)",marginBottom:16,overflow:"hidden"}}>
            {/* Phase header (collapsible) */}
            <div onClick={()=>togglePhase(phaseIdx)}
              style={{padding:"14px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",borderBottom:phase.collapsed?"none":"1px solid var(--stone-200)",background:phase.collapsed?"var(--stone-50)":"rgba(201,169,110,.03)",transition:"all .2s",userSelect:"none"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:10,color:"var(--stone-400)",transition:"transform .2s",transform:phase.collapsed?"rotate(-90deg)":"rotate(0)"}}>▼</span>
                <span style={{fontSize:20}}>{phase.icon}</span>
                <div>
                  <span style={{fontFamily:"var(--font-display)",fontSize:17,fontWeight:700}}>{phase.wbs}. {phase.name}</span>
                  <span style={{fontSize:11,color:"var(--stone-400)",marginLeft:8}}>{phase.items.length} item{phase.items.length!==1?"s":""}</span>
                  {pLaborBudget>0 || pLaborActual>0 ? (
                    <span style={{fontSize:10,color:"var(--warm-gold)",marginLeft:6,fontWeight:600}}>⚡ Labor: {fmt(pLaborActual)}</span>
                  ) : null}
                </div>
              </div>
              <div style={{display:"flex",gap:16,alignItems:"center",textAlign:"right"}}>
                <div>
                  <div style={{fontSize:10,color:"var(--stone-400)",textTransform:"uppercase",letterSpacing:".05em"}}>Budget</div>
                  <div style={{fontFamily:"var(--font-display)",fontSize:16,fontWeight:700,color:"var(--stone-600)"}}>{fmt(pTotalBudget)}</div>
                  {pLaborBudget>0 && <div style={{fontSize:9,color:"var(--warm-gold)",fontWeight:600}}>mat {fmt(pBudget)} + labor {fmt(pLaborBudget)}</div>}
                </div>
                <div>
                  <div style={{fontSize:10,color:"var(--stone-400)",textTransform:"uppercase",letterSpacing:".05em"}}>Actual</div>
                  <div style={{fontFamily:"var(--font-display)",fontSize:16,fontWeight:700,color:over?"var(--accent-red)":"var(--accent-green)"}}>{fmt(pTotalActual)}</div>
                  {pLaborActual>0 && <div style={{fontSize:9,color:"var(--warm-gold)",fontWeight:600}}>mat {fmt(pActual)} + labor {fmt(pLaborActual)}</div>}
                </div>
                <div style={{width:70}}>
                  <div style={{fontSize:10,color:"var(--stone-400)",textTransform:"uppercase",letterSpacing:".05em"}}>Variance</div>
                  <div style={{fontFamily:"var(--font-display)",fontSize:16,fontWeight:700,color:pVariance>=0?"var(--accent-green)":"var(--accent-red)"}}>
                    {pVariance>=0?"+":""}{fmt(pVariance)}
                  </div>
                </div>
              </div>
            </div>

            {/* Phase items table */}
            {!phase.collapsed && (
              <div>
                {/* ── Phase Labor Row ── */}
                <div style={{padding:"10px 20px",background:"rgba(201,169,110,.05)",borderBottom:"1px solid var(--stone-200)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                      <span style={{fontSize:14}}>⚡</span>
                      <span style={{fontSize:12,fontWeight:700,color:"var(--warm-gold)",textTransform:"uppercase",letterSpacing:".04em"}}>Phase Labor</span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{fontSize:10,fontWeight:600,color:"var(--stone-500)",textTransform:"uppercase",letterSpacing:".04em"}}>Budget $</div>
                      <input type="number" value={phase.laborBudget||""} onClick={e=>e.stopPropagation()}
                        onChange={e=>{e.stopPropagation();updatePhaseLabor(phaseIdx,"laborBudget",e.target.value)}}
                        placeholder="0" style={{width:110,padding:"6px 10px",fontSize:13,fontWeight:600,textAlign:"right",background:"white",border:"1.5px solid var(--stone-300)",borderRadius:6}} />
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{fontSize:10,fontWeight:600,color:"var(--stone-500)",textTransform:"uppercase",letterSpacing:".04em"}}>Actual $</div>
                      <input type="number" value={phase.laborActual||""} onClick={e=>e.stopPropagation()}
                        onChange={e=>{e.stopPropagation();updatePhaseLabor(phaseIdx,"laborActual",e.target.value)}}
                        placeholder="0"
                        disabled={(phase.laborBreakdown||[]).length>0}
                        title={(phase.laborBreakdown||[]).length>0?"Auto-summed from installer assignments below":""}
                        style={{width:110,padding:"6px 10px",fontSize:13,fontWeight:600,textAlign:"right",
                          background:(phase.laborBreakdown||[]).length>0?"var(--stone-100)":"white",
                          border:"1.5px solid var(--stone-300)",borderRadius:6,
                          color:(phase.laborBreakdown||[]).length>0?"var(--warm-gold)":"var(--stone-900)"}} />
                    </div>
                    {(pLaborBudget>0 || pLaborActual>0) && (() => {
                      const lDiff = pLaborBudget - pLaborActual;
                      return (
                        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:10}}>
                          <span style={{fontSize:12,fontWeight:700,fontFamily:"var(--font-display)",color:lDiff>=0?"var(--accent-green)":"var(--accent-red)"}}>
                            {lDiff>=0?"+":""}{fmt(lDiff)}
                          </span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* ── Installer Breakdown Rows ── */}
                  {(phase.laborBreakdown||[]).length>0 && (
                    <div style={{marginTop:8,marginLeft:28}}>
                      {(phase.laborBreakdown||[]).map((bd,bdIdx)=>(
                        <div key={bdIdx} style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                          <span style={{fontSize:11,color:"var(--stone-400)",width:14}}>👷</span>
                          <select value={bd.installer||""} onClick={e=>e.stopPropagation()}
                            onChange={e=>{e.stopPropagation();updateLaborBreakdown(phaseIdx,bdIdx,"installer",e.target.value)}}
                            style={{width:160,padding:"5px 8px",fontSize:12,border:"1.5px solid var(--stone-300)",borderRadius:6,background:"white",fontFamily:"var(--font-body)"}}>
                            <option value="">Select installer…</option>
                            {installers.map(u=><option key={u.id} value={u.name}>{u.name}</option>)}
                          </select>
                          <input type="number" value={bd.amount||""} placeholder="$0" onClick={e=>e.stopPropagation()}
                            onChange={e=>{e.stopPropagation();updateLaborBreakdown(phaseIdx,bdIdx,"amount",e.target.value)}}
                            style={{width:100,padding:"5px 8px",fontSize:12,fontWeight:600,textAlign:"right",border:"1.5px solid var(--stone-300)",borderRadius:6,background:"white"}} />
                          <button onClick={e=>{e.stopPropagation();removeLaborBreakdown(phaseIdx,bdIdx)}}
                            style={{background:"none",border:"none",cursor:"pointer",fontSize:13,color:"var(--accent-red)",padding:"2px 4px"}}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={e=>{e.stopPropagation();addLaborBreakdown(phaseIdx)}}
                    style={{marginTop:6,marginLeft:28,background:"none",border:"none",cursor:"pointer",fontSize:11,fontWeight:600,color:"var(--warm-gold)",display:"flex",alignItems:"center",gap:4}}>
                    + Assign Installer
                  </button>
                </div>

                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead>
                      <tr style={{background:"var(--stone-100)"}}>
                        <th style={thS}>Item</th>
                        <th style={thS}>Category</th>
                        <th style={thS}>Supplier</th>
                        <th style={{...thS,textAlign:"center",width:60}}>Bud Qty</th>
                        <th style={{...thS,textAlign:"right",width:90}}>Bud $/Unit</th>
                        <th style={{...thS,textAlign:"right",width:90,background:"rgba(107,140,174,.06)"}}>Budgeted</th>
                        <th style={{...thS,textAlign:"center",width:60}}>Act Qty</th>
                        <th style={{...thS,textAlign:"right",width:90}}>Act $/Unit</th>
                        <th style={{...thS,textAlign:"right",width:90,background:"rgba(122,158,126,.06)"}}>Actual</th>
                        <th style={{...thS,textAlign:"right",width:90}}>Over/Under</th>
                        <th style={{...thS,width:40}}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {phase.items.length===0 ? (
                        <tr><td colSpan="11" style={{textAlign:"center",padding:28,color:"var(--stone-400)",fontSize:13}}>No items in this phase yet. Add from catalog or manually.</td></tr>
                      ) : phase.items.map((item) => {
                        const ci = CATALOG_CATS[item.cat]||CATALOG_CATS["Other"];
                        const budgetLine = lt(item.budgetQty,item.budgetPrice);
                        const actualLine = lt(item.actualQty,item.actualPrice);
                        const diff = budgetLine-actualLine;
                        const diffColor = actualLine===0?"var(--stone-300)":diff>=0?"var(--accent-green)":"var(--accent-red)";

                        return (
                          <tr key={item.id} onMouseEnter={e=>e.currentTarget.style.background="var(--stone-100)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <td style={tdS}>
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                {item.catalogId && <span title="From catalog" style={{fontSize:9,background:"rgba(201,169,110,.15)",color:"var(--warm-gold)",padding:"1px 5px",borderRadius:4,fontWeight:700}}>CAT</span>}
                                <EditableText value={item.name} onChange={v=>updateItem(phaseIdx,item.id,"name",v)} style={{fontWeight:600,fontSize:13}} />
                              </div>
                            </td>
                            <td style={tdS}>
                              <span style={{background:ci.bg,color:ci.color,padding:"2px 8px",borderRadius:99,fontSize:10,fontWeight:600,whiteSpace:"nowrap"}}>{ci.icon} {item.cat}</span>
                            </td>
                            <td style={{...tdS,fontSize:11,color:"var(--stone-500)"}}>
                              <EditableText value={item.supplier} onChange={v=>updateItem(phaseIdx,item.id,"supplier",v)} style={{fontSize:11,color:"var(--stone-500)"}} />
                            </td>
                            <td style={{...tdS,textAlign:"center"}}>
                              <EditableNum value={item.budgetQty} onChange={v=>updateItem(phaseIdx,item.id,"budgetQty",v)} />
                            </td>
                            <td style={{...tdS,textAlign:"right"}}>
                              <EditableNum value={item.budgetPrice} onChange={v=>updateItem(phaseIdx,item.id,"budgetPrice",v)} prefix="$" />
                            </td>
                            <td style={{...tdS,textAlign:"right",fontFamily:"var(--font-display)",fontSize:14,fontWeight:700,color:"var(--accent-blue)",background:"rgba(107,140,174,.04)"}}>{fmt(budgetLine)}</td>
                            <td style={{...tdS,textAlign:"center"}}>
                              <EditableNum value={item.actualQty} onChange={v=>updateItem(phaseIdx,item.id,"actualQty",v)} />
                            </td>
                            <td style={{...tdS,textAlign:"right"}}>
                              <EditableNum value={item.actualPrice} onChange={v=>updateItem(phaseIdx,item.id,"actualPrice",v)} prefix="$" />
                            </td>
                            <td style={{...tdS,textAlign:"right",fontFamily:"var(--font-display)",fontSize:14,fontWeight:700,color:actualLine>0?"var(--accent-green)":"var(--stone-300)",background:"rgba(122,158,126,.04)"}}>{actualLine>0?fmt(actualLine):"—"}</td>
                            <td style={{...tdS,textAlign:"right",fontFamily:"var(--font-display)",fontSize:14,fontWeight:700,color:diffColor}}>
                              {actualLine===0?"—":(diff>=0?"+":"")+fmt(diff)}
                            </td>
                            <td style={{...tdS,textAlign:"center"}}>
                              <button onClick={()=>deleteItem(phaseIdx,item.id)} title="Remove" style={{padding:3,background:"none",border:"none",cursor:"pointer",fontSize:12,opacity:.4,transition:"opacity .2s"}}
                                onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=.4}>🗑</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Phase action buttons */}
                <div style={{padding:"10px 16px",borderTop:"1px solid var(--stone-100)",display:"flex",gap:8,background:"var(--stone-50)"}}>
                  <button onClick={()=>{loadCatalog();setCatalogOpen(phaseIdx);setCatalogSearch("");setCatalogCat("All")}} style={btnSmGold}>📋 Add from Catalog</button>
                  <button onClick={()=>addManualItem(phaseIdx)} style={btnSmGhost}>＋ Manual Item</button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add phase button */}
      <button onClick={addPhase} style={{...btnSmGhost,marginBottom:20,padding:"12px 20px",fontSize:13,border:"2px dashed var(--stone-300)",borderRadius:"var(--radius-lg)",width:"100%",justifyContent:"center",display:"flex",alignItems:"center",gap:6}}>
        ＋ Add Phase Section
      </button>

      {/* ════ BREAKDOWN CARDS ════ */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
        {/* By Phase */}
        <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",boxShadow:"var(--shadow-sm)",padding:0,overflow:"hidden"}}>
          <div style={{padding:"14px 18px",borderBottom:"1px solid var(--stone-100)",fontFamily:"var(--font-display)",fontSize:16,fontWeight:700}}>By Phase</div>
          <div style={{padding:"14px 18px"}}>
            {totals.byPhase?.filter(p=>p.budget>0||p.actual>0||p.laborBudget>0||p.laborActual>0).map((ph,i)=>{
              const totalPhaseActual = ph.actual + ph.laborActual;
              const totalAllActual = totals.actualTotal + totals.laborActualTotal;
              const pct = totalAllActual>0?Math.round(totalPhaseActual/totalAllActual*100):0;
              return(
                <div key={i} style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                    <span style={{fontSize:12,fontWeight:600}}>{ph.icon} {ph.name}</span>
                    <span style={{fontSize:12,fontWeight:700,color:"var(--stone-700)"}}>{fmt(totalPhaseActual)}</span>
                  </div>
                  <div style={{height:6,background:"var(--stone-100)",borderRadius:99,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:"var(--warm-gold)",borderRadius:99}}/>
                  </div>
                  <div style={{fontSize:10,color:"var(--stone-400)",marginTop:2}}>
                    {pct}% of spend · Mat: {fmt(ph.actual)}
                    {ph.laborActual>0 && <span style={{color:"var(--warm-gold)"}}> · Labor: {fmt(ph.laborActual)}</span>}
                    {' '}· {ph.count} items
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* By Category */}
        <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"1px solid var(--stone-200)",boxShadow:"var(--shadow-sm)",padding:0,overflow:"hidden"}}>
          <div style={{padding:"14px 18px",borderBottom:"1px solid var(--stone-100)",fontFamily:"var(--font-display)",fontSize:16,fontWeight:700}}>By Material Category</div>
          <div style={{padding:"14px 18px"}}>
            {Object.entries(totals.byCat||{}).filter(([_,v])=>v.actual>0||v.budget>0).sort((a,b)=>b[1].actual-a[1].actual).map(([cat,v])=>{
              const ci = CATALOG_CATS[cat]||CATALOG_CATS["Other"];
              const pct = totals.actualTotal>0?Math.round(v.actual/totals.actualTotal*100):0;
              return(
                <div key={cat} style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                    <span style={{fontSize:12,fontWeight:600}}>{ci.icon} {cat}</span>
                    <span style={{fontSize:12,fontWeight:700,color:ci.color}}>{fmt(v.actual)}</span>
                  </div>
                  <div style={{height:6,background:"var(--stone-100)",borderRadius:99,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:ci.color,borderRadius:99}}/>
                  </div>
                  <div style={{fontSize:10,color:"var(--stone-400)",marginTop:2}}>{pct}% · Budget: {fmt(v.budget)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ════ PROJECT FINANCIALS FOOTER (mirrors Excel bottom) ════ */}
      <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",border:"2px solid var(--stone-200)",boxShadow:"var(--shadow-sm)",padding:24,marginBottom:20}}>
        <div style={{fontFamily:"var(--font-display)",fontSize:18,fontWeight:700,marginBottom:16}}>📊 Project Financials</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr auto auto",gap:"8px 24px",alignItems:"center"}}>
          {[
            ["Original Quote",fmt(proj.originalQuote),"var(--stone-700)"],
            ["Materials Budget (with markup)",fmt(totals.budgetTotal),"var(--accent-blue)"],
            ["Actual Material Spend",fmt(totals.actualTotal),totals.actualTotal>totals.budgetTotal?"var(--accent-red)":"var(--accent-green)"],
            ...(proj.changeOrder>0?[["Change Order",fmt(proj.changeOrder),"var(--accent-amber)"]]:[] ),
          ].map(([label,val,color],i)=>(
            <React.Fragment key={i}>
              <div style={{fontSize:13,color:"var(--stone-600)",fontWeight:500}}>{label}</div>
              <div style={{fontFamily:"var(--font-display)",fontSize:17,fontWeight:700,color,textAlign:"right"}}>{val}</div>
              <div/>
            </React.Fragment>
          ))}

          {/* Labor section */}
          <div style={{gridColumn:"1/-1",borderTop:"1px solid var(--stone-200)",margin:"6px 0 2px"}}/>
          <div style={{fontSize:13,fontWeight:700,color:"var(--warm-gold)"}}>⚡ Total Labor</div>
          <div style={{fontFamily:"var(--font-display)",fontSize:17,fontWeight:700,color:"var(--warm-gold)",textAlign:"right"}}>{fmt(totals.laborCost)}</div>
          <div/>

          {/* Phase labor breakdown */}
          {totals.hasPhaseLabor && totals.byPhase?.filter(ph=>ph.laborBudget>0||ph.laborActual>0).map((ph,i)=>(
            <React.Fragment key={"lab"+i}>
              <div style={{fontSize:12,color:"var(--stone-500)",paddingLeft:20,fontWeight:500}}>
                {ph.icon} {ph.name}
                <span style={{fontSize:10,color:"var(--stone-400)",marginLeft:6}}>budget: {fmt(ph.laborBudget)}</span>
              </div>
              <div style={{fontFamily:"var(--font-display)",fontSize:14,fontWeight:600,color:ph.laborActual>ph.laborBudget&&ph.laborBudget>0?"var(--accent-red)":"var(--warm-gold)",textAlign:"right"}}>{fmt(ph.laborActual)}</div>
              <div style={{fontSize:10,fontWeight:600,color:ph.laborBudget-ph.laborActual>=0?"var(--accent-green)":"var(--accent-red)",textAlign:"right",minWidth:60}}>
                {ph.laborBudget>0||ph.laborActual>0 ? ((ph.laborBudget-ph.laborActual>=0?"+":"")+fmt(ph.laborBudget-ph.laborActual)) : ""}
              </div>
            </React.Fragment>
          ))}

          {/* If no phase labor, show legacy project-level labor */}
          {!totals.hasPhaseLabor && (
            <React.Fragment>
              <div style={{fontSize:12,color:"var(--stone-500)",paddingLeft:20,fontWeight:500}}>
                {proj.laborType==="flat" ? "Flat rate" : `${proj.laborHours||0}h × ${fmt(proj.laborRate||0)}/hr`}
              </div>
              <div/>
              <div/>
            </React.Fragment>
          )}

          <div style={{gridColumn:"1/-1",borderTop:"2px solid var(--stone-200)",margin:"8px 0"}}/>
          <div style={{fontSize:15,fontWeight:700,color:"var(--stone-900)"}}>Project Total</div>
          <div style={{fontFamily:"var(--font-display)",fontSize:24,fontWeight:700,color:"var(--stone-900)",textAlign:"right"}}>{fmt(totals.quoteTotal)}</div>
          <div/>
          <div style={{fontSize:13,color:totals.grossProfit>0?"var(--accent-green)":"var(--accent-red)",fontWeight:700}}>
            Gross Profit: {fmt(totals.grossProfit)} ({totals.margin}% margin)
          </div>
        </div>

        {/* Editable fields */}
        <div style={{marginTop:20,display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
          <div>
            <label style={labelS}>Original Quote</label>
            <input type="number" value={proj.originalQuote||""} onChange={e=>setProj(p=>({...p,originalQuote:Number(e.target.value)}))} style={{fontSize:13}} />
          </div>
          <div>
            <label style={labelS}>Change Order</label>
            <input type="number" value={proj.changeOrder||""} onChange={e=>setProj(p=>({...p,changeOrder:Number(e.target.value)}))} style={{fontSize:13}} />
          </div>
          <div>
            <label style={labelS}>Labor Entry</label>
            <div style={{fontSize:12,color:"var(--stone-500)",padding:"8px 10px",background:"var(--stone-100)",borderRadius:"var(--radius)",border:"1.5px solid var(--stone-200)"}}>
              {totals.hasPhaseLabor
                ? <span>⚡ Set per-phase above · Total: <strong style={{color:"var(--warm-gold)"}}>{fmt(totals.laborCost)}</strong></span>
                : <span style={{color:"var(--stone-400)"}}>Add labor in each phase section above</span>
              }
            </div>
          </div>
        </div>
      </div>
      </>)}

      {/* ════ CATALOG PICKER MODAL ════ */}
      {proj && catalogOpen !== null && (
        <div style={{position:"fixed",inset:0,background:"rgba(26,23,20,.55)",backdropFilter:"blur(4px)",zIndex:1000,overflow:"auto",display:"flex",justifyContent:"center",alignItems:"flex-start",padding:"40px 20px 80px"}}>
          <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",padding:0,width:"100%",maxWidth:680,boxShadow:"var(--shadow-lg)",animation:"modalIn .25s ease",position:"relative",margin:"auto 0",overflow:"hidden"}}>
            <div style={{padding:"20px 24px",borderBottom:"1px solid var(--stone-200)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <h2 style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700,margin:0}}>Add from Catalog</h2>
                <p style={{fontSize:12,color:"var(--stone-400)",margin:0}}>Adding to: {proj.phases[catalogOpen]?.icon} {proj.phases[catalogOpen]?.name} · {catalogLoading ? "loading…" : `${catalogItems.length} items available`}</p>
              </div>
              <button onClick={()=>setCatalogOpen(null)} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--stone-400)"}}>✕</button>
            </div>
            <div style={{padding:"12px 24px",borderBottom:"1px solid var(--stone-100)",display:"flex",gap:8}}>
              <input value={catalogSearch} onChange={e=>setCatalogSearch(e.target.value)} placeholder="Search catalog..." autoFocus
                style={{flex:1,padding:"8px 12px",fontSize:13}} />
              <select value={catalogCat} onChange={e=>setCatalogCat(e.target.value)} style={{padding:"8px 10px",fontSize:12,fontWeight:600,width:160}}>
                <option value="All">All Categories</option>
                {Object.keys(CATALOG_CATS).map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{maxHeight:400,overflowY:"auto"}}>
              {catalogLoading && catalogItems.length === 0 ? (
                <div style={{padding:"32px 24px",textAlign:"center",color:"var(--stone-400)",fontSize:13}}>
                  <div style={{fontSize:24,marginBottom:8}}>📦</div>
                  Loading catalog from database…
                </div>
              ) : catalogItems.length === 0 ? (
                <div style={{padding:"32px 24px",textAlign:"center",color:"var(--stone-400)",fontSize:13}}>
                  <div style={{fontSize:24,marginBottom:8}}>📦</div>
                  No catalog items found.
                  <div style={{fontSize:11,marginTop:8}}>Visit the Materials Catalog page to add items.</div>
                </div>
              ) : (() => {
                const matches = catalogItems.filter(ci=>{
                  if(catalogCat!=="All"&&ci.cat!==catalogCat) return false;
                  if(catalogSearch){const s=catalogSearch.toLowerCase();return ci.name.toLowerCase().includes(s)||ci.cat.toLowerCase().includes(s)||ci.supplier.toLowerCase().includes(s)}
                  return true;
                });
                if (matches.length === 0) return (
                  <div style={{padding:"32px 24px",textAlign:"center",color:"var(--stone-400)",fontSize:13}}>
                    No items match your search. Try different keywords or category.
                  </div>
                );
                return matches.map(ci=>{
                const catInfo = CATALOG_CATS[ci.cat]||CATALOG_CATS["Other"];
                const bid = ci.price*(1+ci.upcharge/100);
                return(
                  <div key={ci.id} onClick={()=>addFromCatalog(ci,catalogOpen)}
                    style={{padding:"12px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",borderBottom:"1px solid var(--stone-100)",transition:"background .15s"}}
                    onMouseEnter={e=>e.currentTarget.style.background="var(--stone-100)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:32,height:32,borderRadius:6,background:catInfo.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{catInfo.icon}</div>
                      <div>
                        <div style={{fontWeight:600,fontSize:13}}>{ci.name}</div>
                        <div style={{fontSize:11,color:"var(--stone-400)"}}>{ci.supplier} · {ci.unit}</div>
                      </div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{fontFamily:"var(--font-display)",fontSize:15,fontWeight:700,color:"var(--warm-gold)"}}>{fmt(bid)}</div>
                      <div style={{fontSize:10,color:"var(--stone-400)"}}>Cost: {fmt(ci.price)} · +{ci.upcharge}%</div>
                    </div>
                  </div>
                );
              })})()}
            </div>
          </div>
        </div>
      )}

      {/* ════ NEW PROJECT MODAL ════ */}
      {newProjectModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(26,23,20,.55)",backdropFilter:"blur(4px)",zIndex:1000,overflow:"auto",display:"flex",justifyContent:"center",alignItems:"flex-start",padding:"40px 20px 80px"}}>
          <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",padding:32,width:"100%",maxWidth:520,boxShadow:"var(--shadow-lg)",animation:"modalIn .25s ease",margin:"auto 0"}}>
            <h2 style={{fontFamily:"var(--font-display)",fontSize:22,fontWeight:700,marginBottom:20}}>New Project Budget</h2>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <div style={{gridColumn:"1/-1"}}><label style={labelS}>Project Name</label><input value={npForm.name} onChange={e=>setNpForm(p=>({...p,name:e.target.value}))} placeholder="e.g., Sarah Double Bath" /></div>
              <div><label style={labelS}>Customer</label><input value={npForm.customer} onChange={e=>setNpForm(p=>({...p,customer:e.target.value}))} placeholder="Customer name" /></div>
              <div><label style={labelS}>Location</label><input value={npForm.location} onChange={e=>setNpForm(p=>({...p,location:e.target.value}))} placeholder="Address" /></div>
              <div>
                <label style={labelS}>Project Type</label>
                <select value={npForm.type} onChange={e=>setNpForm(p=>({...p,type:e.target.value}))}>
                  <option value="bath">🚿 Bathroom</option>
                  <option value="kitchen">🍳 Kitchen</option>
                  <option value="custom">📦 Custom</option>
                </select>
              </div>
              <div><label style={labelS}>Original Quote ($)</label><input type="number" value={npForm.originalQuote} onChange={e=>setNpForm(p=>({...p,originalQuote:e.target.value}))} placeholder="15000" /></div>

              {/* Labor info */}
              <div style={{gridColumn:"1/-1"}}>
                <label style={labelS}>Labor</label>
                <div style={{padding:"10px 14px",background:"rgba(201,169,110,.06)",borderRadius:"var(--radius)",border:"1.5px solid rgba(201,169,110,.15)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--warm-gold)",fontWeight:600}}>
                    <span>⚡</span> Labor costs are tracked per phase
                  </div>
                  <div style={{fontSize:11,color:"var(--stone-500)",marginTop:4}}>
                    After creating the project, set labor budget and actual cost in each phase section (e.g., Tile Installation, Fixtures, etc.)
                  </div>
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:24}}>
              <button onClick={()=>setNewProjectModal(false)} style={{...btnPrimary,background:"var(--stone-200)",color:"var(--stone-800)"}}>Cancel</button>
              <button onClick={createProject} style={btnPrimary}>Create Project</button>
            </div>
          </div>
        </div>
      )}

      {/* ════ SEED FROM ESTIMATE MODAL ════ */}
      {seedModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(26,23,20,.55)",backdropFilter:"blur(4px)",zIndex:1000,overflow:"auto",display:"flex",justifyContent:"center",alignItems:"flex-start",padding:"40px 20px 80px"}}>
          <div style={{background:"var(--stone-50)",borderRadius:"var(--radius-lg)",padding:0,width:"100%",maxWidth:620,boxShadow:"var(--shadow-lg)",animation:"modalIn .25s ease",margin:"auto 0",overflow:"hidden"}}>
            <div style={{padding:"20px 24px",borderBottom:"1px solid var(--stone-200)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <h2 style={{fontFamily:"var(--font-display)",fontSize:20,fontWeight:700,margin:0}}>Seed Budget from Estimate</h2>
                <p style={{fontSize:12,color:"var(--stone-400)",margin:0}}>Pick any estimate to auto-create a project budget — no job conversion needed</p>
              </div>
              <button onClick={()=>setSeedModal(false)} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--stone-400)"}}>✕</button>
            </div>
            <div style={{maxHeight:450,overflowY:"auto"}}>
              {seedLoading ? (
                <div style={{padding:40,textAlign:"center",color:"var(--stone-400)",fontSize:14}}>Loading estimates…</div>
              ) : seedableEstimates.length === 0 ? (
                <div style={{padding:40,textAlign:"center"}}>
                  {totalEstimateCount === 0 ? (
                    <>
                      <div style={{fontSize:28,marginBottom:8}}>📝</div>
                      <div style={{fontSize:14,color:"var(--stone-500)",fontWeight:600}}>No estimates yet</div>
                      <div style={{fontSize:12,color:"var(--stone-400)",marginTop:4}}>Create an estimate first, then come back to seed a budget from it</div>
                    </>
                  ) : (
                    <>
                      <div style={{fontSize:28,marginBottom:8}}>✅</div>
                      <div style={{fontSize:14,color:"var(--stone-500)",fontWeight:600}}>All estimates are already linked to a budget</div>
                      <div style={{fontSize:12,color:"var(--stone-400)",marginTop:4}}>Each estimate can seed one budget. Create a new estimate to seed another.</div>
                    </>
                  )}
                </div>
              ) : seedableEstimates.map(item => (
                <div key={item.est.id} onClick={()=>seedFromEstimate(item)}
                  style={{padding:"14px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",borderBottom:"1px solid var(--stone-100)",transition:"background .15s"}}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--stone-100)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{fontWeight:700,fontSize:14}}>{item.est.project || "Untitled"}</div>
                    <div style={{fontSize:12,color:"var(--stone-500)",marginTop:2}}>
                      {item.est.customer} · {item.est.id}
                      {item.est.status ? ` · ${item.est.status}` : ""}
                      {item.address ? ` · ${item.address}` : ""}
                    </div>
                    <div style={{fontSize:11,color:"var(--stone-400)",marginTop:2}}>
                      {(item.est.items||[]).length} scope items · {item.matCount} materials
                    </div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
                    <div style={{fontFamily:"var(--font-display)",fontSize:18,fontWeight:700,color:"var(--warm-gold)"}}>{fmt(item.total)}</div>
                    <div style={{fontSize:10,color:"var(--accent-green)",fontWeight:600,marginTop:2}}>Click to seed</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ════ TOAST ════ */}
      {toast && (
        <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",background:"var(--stone-800)",color:"white",borderRadius:"var(--radius)",padding:"10px 20px",fontSize:13,fontWeight:500,boxShadow:"0 4px 16px rgba(0,0,0,.3)",animation:"modalIn .3s ease",zIndex:2000,display:"flex",alignItems:"center",gap:8}}>
          <span style={{color:"var(--warm-gold)"}}>✓</span> {toast}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  INLINE EDITABLE COMPONENTS
// ══════════════════════════════════════════════════════════════
function EditableText({value,onChange,style={}}) {
  const [editing,setEditing] = useState(false);
  const [temp,setTemp] = useState(value);
  const ref = useRef(null);
  useEffect(()=>{if(editing&&ref.current)ref.current.focus()},[editing]);
  if(editing) return <input ref={ref} value={temp} onChange={e=>setTemp(e.target.value)} onBlur={()=>{onChange(temp);setEditing(false)}} onKeyDown={e=>{if(e.key==="Enter"){onChange(temp);setEditing(false)}if(e.key==="Escape")setEditing(false)}}
    style={{...style,padding:"2px 4px",border:"1px solid var(--warm-gold)",borderRadius:4,background:"white",width:"100%",outline:"none"}} />;
  return <span onClick={()=>{setTemp(value);setEditing(true)}} style={{...style,cursor:"text",borderBottom:"1px dashed transparent",transition:"border-color .2s"}}
    onMouseEnter={e=>e.currentTarget.style.borderBottomColor="var(--stone-300)"} onMouseLeave={e=>e.currentTarget.style.borderBottomColor="transparent"}>{value||"—"}</span>;
}

function EditableNum({value,onChange,prefix=""}) {
  const [editing,setEditing] = useState(false);
  const [temp,setTemp] = useState(value);
  const ref = useRef(null);
  useEffect(()=>{if(editing&&ref.current){ref.current.focus();ref.current.select()}},[editing]);
  if(editing) return <input ref={ref} type="number" step="any" value={temp} onChange={e=>setTemp(e.target.value)} onBlur={()=>{onChange(Number(temp)||0);setEditing(false)}} onKeyDown={e=>{if(e.key==="Enter"){onChange(Number(temp)||0);setEditing(false)}if(e.key==="Escape")setEditing(false)}}
    style={{width:70,padding:"2px 4px",border:"1px solid var(--warm-gold)",borderRadius:4,background:"white",fontSize:12,textAlign:"right",outline:"none",fontFamily:"var(--font-body)"}} />;
  return <span onClick={()=>{setTemp(value);setEditing(true)}} style={{cursor:"text",fontSize:12,borderBottom:"1px dashed transparent",transition:"border-color .2s"}}
    onMouseEnter={e=>e.currentTarget.style.borderBottomColor="var(--stone-300)"} onMouseLeave={e=>e.currentTarget.style.borderBottomColor="transparent"}>{prefix}{Number(value||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</span>;
}

// ══════════════════════════════════════════════════════════════
//  SHARED STYLES
// ══════════════════════════════════════════════════════════════
const thS = {textAlign:"left",padding:"8px 12px",fontSize:10,fontWeight:600,letterSpacing:".06em",textTransform:"uppercase",color:"#8c7b6e",borderBottom:"1px solid #e8e0d8",whiteSpace:"nowrap"};
const tdS = {padding:"10px 12px",fontSize:13,borderBottom:"1px solid #f4f0eb",verticalAlign:"middle"};
const labelS = {fontSize:11,fontWeight:600,color:"#6b5e52",letterSpacing:".05em",textTransform:"uppercase",display:"block",marginBottom:4};
const btnPrimary = {display:"inline-flex",alignItems:"center",gap:6,padding:"10px 20px",borderRadius:8,fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600,cursor:"pointer",border:"none",background:"#c9a96e",color:"white",transition:"all .2s"};
const btnSmGold = {display:"inline-flex",alignItems:"center",gap:4,padding:"6px 14px",borderRadius:6,fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:600,cursor:"pointer",border:"none",background:"rgba(201,169,110,.12)",color:"#c9a96e",transition:"all .2s"};
const btnSmGhost = {display:"inline-flex",alignItems:"center",gap:4,padding:"6px 14px",borderRadius:6,fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:500,cursor:"pointer",border:"none",background:"transparent",color:"#8c7b6e",transition:"all .2s"};
